import config from '../config';
import { rateLimitManager } from './security';
import { adminSocket } from './admin-socket';
import { ADMIN_LOGIN_PATH, isAdminLoginPath } from './auth-paths';
import type { Document } from '@/types/document';
import {
  putFileToPresignedUrl,
  validateNavImageFile,
} from '@/lib/api/navImageUpload';

// Use centralized configuration
const API_URL = config.apiUrl;
const ADMIN_ENDPOINTS = config.endpoints.admin;

const REQUEST_ID_HEADER = 'X-Request-ID';
const REQUEST_ID_PATTERN = /^[A-Za-z0-9_-]{6,64}$/;

/**
 * Correlation id the API stamps on every response header, error body, and log
 * line. Surfacing it lets a screenshot of a failed admin action be traced to the
 * exact server-side error.
 */
function extractRequestId(response: Response, body: unknown): string | undefined {
    const candidates = [
        response.headers.get(REQUEST_ID_HEADER),
        typeof body === 'object' && body !== null
            ? (body as { requestId?: unknown }).requestId
            : undefined,
    ];

    for (const candidate of candidates) {
        if (typeof candidate === 'string' && REQUEST_ID_PATTERN.test(candidate.trim())) {
            return candidate.trim();
        }
    }

    return undefined;
}

// Add validation for API URL
if (!API_URL) {
    console.error('API URL is not configured. Please set VITE_API_URL environment variable.');
}

// Validate URL format
try {
    new URL(API_URL);
} catch (e) {
    console.error('Invalid API URL format:', API_URL);
}

console.log('Using API URL:', API_URL);
console.log(`Environment: ${config.isDevelopment ? 'Local Development' : 'Production/Deployed'}`);
console.log(`Using admin endpoints:`, ADMIN_ENDPOINTS);

type ApiOptions = {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  skipAuthRefresh?: boolean; // Skip auth refresh for certain requests like logout
  retryCount?: number; // Add retry count for rate limiting
};

// Certifications API
export type CertificationMode = 'online' | 'hybrid' | 'on-campus';

export type CertificationStatusKey = 'COMPLETED' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Certification {
  _id?: string;
  id?: string;
  /** Student document id used for portal impersonation. */
  studentId?: string;
  username: string; // Student's full name (prioritizes UserData.fullName)
  email: string;
  startDate?: string;
  programName?: string;
  mode?: CertificationMode;
  theory?: { key: 'COMPLETED' | 'PENDING'; value: boolean };
  clinical?: { key: 'COMPLETED' | 'PENDING' | 'NOT_REQUIRED'; value: boolean };
  payment?: { key: 'COMPLETED' | 'PENDING'; value: boolean };
  documentation?: { key: 'APPROVED' | 'PENDING'; value: boolean };
  graduation?: { key: 'APPROVED' | 'PENDING'; value: boolean; date?: string };
  digitalBadge?: { 
    status?: 'APPROVED' | 'REJECTED' | 'PENDING' | 'ISSUED';
    approvedAt?: string;
    approvedBy?: string;
    rejectionReason?: string;
    downloadedAt?: string; 
    url?: string; 
  };
  letter?: { 
    status?: 'APPROVED' | 'REJECTED' | 'PENDING' | 'ISSUED';
    approvedAt?: string;
    approvedBy?: string;
    rejectionReason?: string;
    downloadedAt?: string; 
    url?: string; 
  };
  transcript?: string;
  certificate?: { 
    status?: 'APPROVED' | 'REJECTED' | 'PENDING' | 'ISSUED';
    approvedAt?: string;
    approvedBy?: string;
    rejectionReason?: string;
    downloadedAt?: string; 
    url?: string; 
  };
  certificateNumber?: string;
  /** Current enrollment program from student profile / latest order (not overwritten onto cert). */
  enrolledProgram?: string;
  // New tracking fields
  lastLogin?: string;
  lastCertificateDownload?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Allowed update payload for certification updates
export type UpdateCertificationPayload = Partial<{
  theory: { key: 'COMPLETED' | 'PENDING'; value: boolean };
  clinical: { key: 'COMPLETED' | 'PENDING' | 'NOT_REQUIRED'; value: boolean };
  payment: { key: 'COMPLETED' | 'PENDING'; value: boolean };
  documentation: { key: 'APPROVED' | 'PENDING'; value: boolean };
  graduation: { key: 'APPROVED' | 'PENDING'; value: boolean; date?: string };
  transcript: string;
  certificateNumber: string;
  /** Printed on the certificate. Use PENDING to fall back to current enrollment. */
  programName: string;
}>;

/** Body for PATCH .../issue, issue-badge, issue-letter when student portal profile is incomplete. */
export type IssueCredentialPayload = {
  overrideProfileIncomplete?: boolean;
  overrideReason?: string;
};

/** Must match Backend-Americation `credentialIssueProfileGate`. */
export const CREDENTIAL_ISSUE_OVERRIDE_REASON_MIN_LEN = 20;

// User data structure from API
export interface UserData {
  _id: string;
  accountId: string;
  email: string;
  fullName: string;
  dateOfBirth?: string;
  cellPhoneNumber?: string;
  isBlocked?: boolean;
  isDeleted?: boolean;
  lastLogin?: string;
  lastCertificateDownload?: string;
  requiresPasswordReset?: boolean;
  programEnrolled?: string;
  graduation?: {
    month: string;
    year: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Function to extract user data and create certification
export const createCertificationFromUser = async (userData: UserData) => {
  const payload = {
    username: userData.fullName,
    email: userData.email,
    programName: userData.programEnrolled || 'Unknown Program',
    mode: 'hybrid' as CertificationMode, // Default mode, can be updated
    graduation: {
      key: 'PENDING' as const,
      value: false,
      date: userData.graduation ? `${userData.graduation.month} ${userData.graduation.year}` : undefined
    }
  };

  return certificationsApi.create(payload);
};

export const certificationsApi = {
  // List all certifications with optional query params
  getAll: (query?: Record<string, string | number | boolean>) => {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      success: boolean;
      count?: number;
      data: Certification[];
    }>(`/api/admin/certifications${qs ? `?${qs}` : ''}`);
  },

  // Get a single certification by username or email
  getByIdentifier: (identifier: string) =>
    apiRequest<{
      success: boolean;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}`),

  // Create certification (admin)
  create: (payload: {
    username: string;
    email: string;
    startDate?: string;
    programName: string;
    mode: CertificationMode;
    theory?: { key: 'COMPLETED' | 'PENDING'; value: boolean };
    clinical?: { key: 'COMPLETED' | 'PENDING'; value: boolean };
    payment?: { key: 'COMPLETED' | 'PENDING'; value: boolean };
    documentation?: { key: 'APPROVED' | 'PENDING'; value: boolean };
    graduation?: { key: 'APPROVED' | 'PENDING'; value: boolean; date?: string };
    transcript?: string;
  }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications`, {
      method: 'POST',
      body: payload,
    }),

  // Admin: update certification by username/email identifier
  updateByIdentifier: (identifier: string, payload: UpdateCertificationPayload) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}`, {
      method: 'PATCH',
      body: payload,
    }),

  // Admin: approve/reject certificate
  approveByIdentifier: (identifier: string, payload: {
    status: 'APPROVED' | 'REJECTED' | 'PENDING';
    rejectionReason?: string;
  }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/approve`, {
      method: 'PATCH',
      body: payload,
    }),

  // Admin: issue certificate for download (optional profile-incomplete override — see Backend `credentialIssueProfileGate`)
  issueCertificate: (identifier: string, body?: IssueCredentialPayload) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/issue`, {
      method: 'PATCH',
      body: body ?? {},
    }),

  // Admin: download certificate PDF
  downloadCertificate: async (identifier: string): Promise<Blob> => {
    const response = await fetch(`${API_URL}/api/admin/certifications/${encodeURIComponent(identifier)}/download`, {
      method: 'GET',
      credentials: 'include', // Use httpOnly cookies for authentication
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to download certificate: ${response.statusText}`);
    }

    return response.blob();
  },

  // Badge API functions
  approveBadgeByIdentifier: (identifier: string, payload: {
    status: 'APPROVED' | 'REJECTED' | 'PENDING';
    rejectionReason?: string;
  }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/approve-badge`, {
      method: 'PATCH',
      body: payload,
    }),

  issueBadge: (identifier: string, body?: IssueCredentialPayload) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/issue-badge`, {
      method: 'PATCH',
      body: body ?? {},
    }),

  downloadBadge: async (identifier: string): Promise<Blob> => {
    const response = await fetch(`${API_URL}/api/admin/certifications/${encodeURIComponent(identifier)}/download-badge`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to download badge: ${response.statusText}`);
    }

    return response.blob();
  },

  // Letter API functions
  approveLetterByIdentifier: (identifier: string, payload: {
    status: 'APPROVED' | 'REJECTED' | 'PENDING';
    rejectionReason?: string;
  }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/approve-letter`, {
      method: 'PATCH',
      body: payload,
    }),

  issueLetter: (identifier: string, body?: IssueCredentialPayload) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Certification;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/issue-letter`, {
      method: 'PATCH',
      body: body ?? {},
    }),

  downloadLetter: async (identifier: string): Promise<Blob> => {
    const response = await fetch(`${API_URL}/api/admin/certifications/${encodeURIComponent(identifier)}/download-letter`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to download letter: ${response.statusText}`);
    }

    return response.blob();
  },

  // Admin: send achievement email to student
  sendAchievementEmail: (identifier: string) =>
    apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/admin/certifications/${encodeURIComponent(identifier)}/send-achievement-email`, {
      method: 'POST',
    }),
};

// Documents API
export const documentsApi = {
  // Get all documents for a specific student
  getByStudentId: (studentId: string) =>
    apiRequest<{
      success: boolean;
      data: {
        student: {
          _id: string;
          fullName?: string;
          firstName?: string;
          lastName?: string;
          email: string;
        };
        documents: Document[];
        summary: {
          total: number;
          uploaded: number;
          required: number;
          optional: number;
          pending: number;
          approved: number;
          rejected: number;
          missingRequired: number;
        };
        missingRequired: Array<{ documentType: string; label: string }>;
      };
    }>(`/api/admin/documents/student/${studentId}`),

  // Get all documents with filtering
  getAll: (query?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      success: boolean;
      data: {
        documents: Document[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(`/api/admin/documents${qs ? `?${qs}` : ''}`);
  },

  // Get document by ID
  getById: (documentId: string) =>
    apiRequest<{
      success: boolean;
      data: {
        document: Document;
        student: {
          _id: string;
          firstName: string;
          lastName: string;
          email: string;
        };
        approvedBy?: {
          _id: string;
          firstName: string;
          lastName: string;
        };
        file: {
          _id: string;
          fileType: string;
          viewUrl: string;
          uploadedAt: string;
        } | null;
        pendingFile?: {
          _id: string;
          fileType: string;
          viewUrl: string;
          uploadedAt: string;
        } | null;
      };
    }>(`/api/admin/documents/${documentId}`),

  // Approve document
  approve: (documentId: string, notes?: string, target: 'document' | 'pending_update' = 'document') =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Document;
    }>(`/api/admin/documents/${documentId}/status`, {
      method: 'PATCH',
      body: { status: 'APPROVED', notes, target },
    }),

  // Reject document
  reject: (
    documentId: string,
    rejectionReason: string,
    notes?: string,
    target: 'document' | 'pending_update' = 'document',
  ) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: Document;
    }>(`/api/admin/documents/${documentId}/status`, {
      method: 'PATCH',
      body: { status: 'REJECTED', rejectionReason, notes, target },
    }),

  // Get document statistics
  getStats: () =>
    apiRequest<{
      success: boolean;
      data: {
        overall: {
          total: number;
          pending: number;
          uploaded: number;
          approved: number;
          rejected: number;
        };
        byType: Array<{
          documentType: string;
          total: number;
          pending: number;
          uploaded: number;
          approved: number;
          rejected: number;
          label: string;
        }>;
      };
    }>('/api/admin/documents/stats'),
};

// Event to notify when auth is invalidated
export const authEvents = {
  onAuthInvalid: new Set<() => void>(),
};

// Add a listener for auth invalidation
export function onAuthInvalid(callback: () => void) {
  authEvents.onAuthInvalid.add(callback);
  return () => {
    authEvents.onAuthInvalid.delete(callback);
  };
}

// Token management (deprecated for cookie-based auth)
// Kept for backward compatibility; now no-ops to avoid storing secrets locally.
const TOKEN_STORAGE_KEY = 'authToken';
const USER_STORAGE_KEY = 'adminInfo';

type TokenData = {
  token: string;
  refreshToken?: string;
  expiresAt?: number;
};

export const tokenService = {
  getToken: (): string | null => {
    return null;
  },
  setTokenData: (_data: TokenData): void => {
    // no-op: we rely on httpOnly cookies now
  },
  clearTokens: (): void => {
    // Clean up any legacy entries if present
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem(USER_STORAGE_KEY);
  },
  isTokenExpired: (): boolean => {
    return false;
  }
};

// Function to notify when auth is invalid
export function notifyAuthInvalid() {
  authEvents.onAuthInvalid.forEach(callback => callback());
}

// Refresh via secure cookies
let isRefreshing = false;
let refreshSubscribers: Array<() => void> = [];

// Global one-time auth retry guard per page load
let hasAuthRetryAttemptedThisLoad = false;


// Function to notify subscribers that token has been refreshed
function onTokenRefreshed() {
  refreshSubscribers.forEach(callback => callback());
  refreshSubscribers = [];
  void adminSocket.reconnectAfterAuthRefresh();
}


// Simple refresh function with activity check and retry logic
export async function refreshAuthToken(): Promise<boolean> {
  // Prevent multiple refresh calls
  if (isRefreshing) {
    return false;
  }
  
  isRefreshing = true;
  
  try {
    const response = await fetch(`${API_URL}${ADMIN_ENDPOINTS.refresh}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    
    if (!response.ok) {
      // Only trigger auth invalidation for actual auth failures (401, 403)
      // Don't logout on server errors (500, 502, 503, etc.)
      if (response.status === 401 || response.status === 403) {
        console.log('Token refresh failed with auth error, notifying auth invalidation');
        notifyAuthInvalid();
      } else {
        console.log(`Token refresh failed with server error (${response.status}), keeping session`);
        // Don't logout on server errors - keep the session
      }
      return false;
    }
    
    // Notify subscribers
    onTokenRefreshed();
    return true;
    
  } catch (error) {
    console.error('Refresh failed:', error);
    
    // Check if this is a network error vs auth error
    if (error instanceof Error && (
      error.message.includes('Failed to fetch') || 
      error.message.includes('websocket') || 
      error.message.includes('TransportError') ||
      error.message.includes('ERR_CONNECTION_REFUSED')
    )) {
      console.log('Network error during refresh, keeping session');
      // Don't logout on network errors - keep the session
    } else {
      console.log('Token refresh error, notifying auth invalidation');
      notifyAuthInvalid();
    }
    return false;
  } finally {
    isRefreshing = false;
  }
}

// Add rate limiting and retry utilities
let requestQueue: Array<() => Promise<any>> = [];
let isProcessingQueue = false;
const MAX_RETRIES = 3;
const BASE_DELAY = 1000; // 1 second

// Authentication state tracking
let isAuthenticating = false;
let authenticationPromise: Promise<any> | null = null;

// Track request timestamps for rate limiting
const requestTimestamps: Record<string, number[]> = {};
const MAX_REQUESTS_PER_MINUTE = 60; // Adjust based on your API limits

// Check if we should throttle requests
function shouldThrottle(endpoint: string): boolean {
  const now = Date.now();
  const oneMinuteAgo = now - 60000; // 1 minute ago
  
  if (!requestTimestamps[endpoint]) {
    requestTimestamps[endpoint] = [];
  }
  
  // Remove old timestamps
  requestTimestamps[endpoint] = requestTimestamps[endpoint].filter(
    timestamp => timestamp > oneMinuteAgo
  );
  
  // Check if we've made too many requests
  if (requestTimestamps[endpoint].length >= MAX_REQUESTS_PER_MINUTE) {
    console.warn(`Rate limiting triggered for ${endpoint}. Requests in last minute: ${requestTimestamps[endpoint].length}`);
    return true;
  }
  
  // Add current timestamp
  requestTimestamps[endpoint].push(now);
  return false;
}

// Debug function to check rate limiting status
export function getRateLimitStatus(endpoint?: string): Record<string, any> {
  if (endpoint) {
    const timestamps = requestTimestamps[endpoint] || [];
    const recentRequests = timestamps.filter(t => t > Date.now() - 60000).length;
    return {
      endpoint,
      recentRequests,
      isThrottled: recentRequests >= MAX_REQUESTS_PER_MINUTE
    };
  }
  
  return {
    allEndpoints: Object.keys(requestTimestamps),
    totalRequests: Object.values(requestTimestamps).reduce((sum, timestamps) => sum + timestamps.length, 0),
    queueLength: requestQueue.length,
    isProcessing: isProcessingQueue
  };
}

// Clear rate limiting data (useful for testing)
export function clearRateLimitData(): void {
  Object.keys(requestTimestamps).forEach(key => {
    delete requestTimestamps[key];
  });
  requestQueue = [];
  isProcessingQueue = false;
}

// Exponential backoff delay
function getRetryDelay(attempt: number): number {
  return BASE_DELAY * Math.pow(2, attempt);
}

// Process request queue with rate limiting
async function processRequestQueue() {
  if (isProcessingQueue || requestQueue.length === 0) return;
  
  isProcessingQueue = true;
  
  while (requestQueue.length > 0) {
    const request = requestQueue.shift();
    if (request) {
      try {
        await request();
        // Add delay between requests to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Request in queue failed:', error);
      }
    }
  }
  
  isProcessingQueue = false;
}

// Add request to queue
function queueRequest<T>(requestFn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    requestQueue.push(async () => {
      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
    
    processRequestQueue();
  });
}

export async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', headers = {}, body, skipAuthRefresh = false, retryCount = 0 } = options;

    const isFormData =
        typeof FormData !== 'undefined' && body != null && body instanceof FormData;

    const requestHeaders: Record<string, string> = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
    };

    // Do not attach Authorization header; rely on httpOnly cookies

    // CSRF protection removed - using secure HTTP-only cookies instead

    const makeRequest = async (): Promise<T> => {
        try {
            // Check if we're currently rate limited
            if (rateLimitManager.isRateLimited()) {
                const timeUntilReset = rateLimitManager.getTimeUntilReset();
                const error = new Error(`Rate limited. Please wait ${Math.ceil(timeUntilReset / 1000)} seconds before trying again.`);
                error.name = 'RateLimitError';
                (error as any).status = 429;
                (error as any).retryAfter = Math.ceil(timeUntilReset / 1000);
                throw error;
            }
            
            // Check if we should throttle this request
            if (shouldThrottle(endpoint)) {
                const error = new Error('Too many requests. Please wait a moment and try again.');
                error.name = 'RateLimitError';
                (error as any).status = 429;
                throw error;
            }
            
            // Ensure proper URL construction
            const url = `${API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
            
            const fetchBody =
                body == null
                    ? undefined
                    : isFormData
                      ? (body as FormData)
                      : JSON.stringify(body);

            console.log('Making API request:', {
                url,
                method,
                headers: requestHeaders,
                body: body == null ? undefined : isFormData ? '[FormData]' : JSON.stringify(body),
            });

            const response = await fetch(url, {
                method,
                headers: requestHeaders,
                body: fetchBody,
                credentials: 'include', // Include cookies in requests
                cache: method === 'GET' && endpoint.includes('/admin/') ? 'no-store' : 'default',
            });

            let data;
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                try {
                    data = await response.json();
                } catch (jsonError) {
                    console.error('JSON parsing error:', jsonError);
                    console.error('Response status:', response.status);
                    console.error('Response headers:', Object.fromEntries(response.headers.entries()));
                    
                    // Try to get the raw response text for debugging
                    const responseText = await response.text();
                    console.error('Raw response text:', responseText);
                    
                    throw new Error(`Invalid JSON response: ${jsonError instanceof Error ? jsonError.message : 'Unknown error'}`);
                }
            } else {
                throw new Error('Invalid response format: expected JSON');
            }

            if (!response.ok) {
                // Handle rate limiting (429) with exponential backoff
                if (response.status === 429 && retryCount < MAX_RETRIES) {
                    const delay = getRetryDelay(retryCount);
                    console.warn(`Rate limited (429). Retrying in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`);
                    
                    // Store rate limit timestamp to prevent immediate retries
                    rateLimitManager.setRateLimited();
                    
                    await new Promise(resolve => setTimeout(resolve, delay));
                    
                    // Retry with incremented retry count
                    return apiRequest(endpoint, { ...options, retryCount: retryCount + 1 });
                }
                
                // If we get 429 and have exhausted retries, throw a specific error
                if (response.status === 429) {
                    const error = new Error('Rate limited - too many requests. Please wait before trying again.');
                    error.name = 'RateLimitError';
                    (error as any).status = 429;
                    (error as any).retryAfter = response.headers.get('retry-after') || 60;
                    throw error;
                }

                // Handle authentication errors
                if (response.status === 401 && !skipAuthRefresh) {
                    // Don't attempt refresh during login process
                    if (isAuthenticating) {
                        throw new Error('Authentication in progress - please wait');
                    }
                    
                    // Classify auth error to avoid bad retries / loops
                    const errorData = data?.error || data?.message || '';
                    const code = data?.code || data?.errorCode || data?.error;
                    const retryHint = response.headers.get('x-auth-retry')?.toLowerCase() || '';
                    const wwwAuth = response.headers.get('www-authenticate')?.toLowerCase() || '';

                    const isTokenExpired = (typeof errorData === 'string' && errorData.includes('expired')) ||
                                           (typeof errorData === 'string' && errorData.includes('Access token has expired')) ||
                                           code === 'AUTH_001' || code === 'TOKEN_EXPIRED';
                    const isTokenMissing = code === 'TOKEN_MISSING' ||
                                           (typeof errorData === 'string' && errorData.includes('not found')) ||
                                           data?.error === 'Access token not found';
                    const isInvalidTokenType = code === 'TOKEN_TYPE_INVALID' ||
                                               (typeof errorData === 'string' && errorData.includes('Invalid token type')) ||
                                               retryHint === 'none' ||
                                               (wwwAuth.includes('invalid_token') && wwwAuth.includes('invalid_token_type'));
                    
                    // Terminal failure: wrong token type – do NOT refresh, notify and stop
                    if (isInvalidTokenType) {
                        notifyAuthInvalid();
                        if (typeof window !== 'undefined') {
                            window.location.href = ADMIN_LOGIN_PATH;
                        }
                        const error = new Error('Authentication failed - invalid token type');
                        error.name = 'AuthError';
                        (error as any).status = 401;
                        throw error;
                    }

                    // Retry once per page load for expired/missing tokens
                    if ((isTokenExpired || isTokenMissing) && !isRefreshing && !hasAuthRetryAttemptedThisLoad) {
                        console.log('Auth 401 classified as retriable once - attempting refresh...');
                        hasAuthRetryAttemptedThisLoad = true;
                        console.log('Token expired, attempting refresh...');
                        const refreshSuccess = await refreshAuthToken();
                        
                        if (refreshSuccess) {
                            // Retry the original request after successful refresh
                            return apiRequest(endpoint, { ...options, retryCount: 0 });
                        } else {
                            // Refresh failed - redirect to login only if not already there
                            const isLoginPage = typeof window !== 'undefined' &&
                              isAdminLoginPath(window.location.pathname);
                            
                            if (!isLoginPage) {
                                notifyAuthInvalid();
                                if (typeof window !== 'undefined') {
                                    window.location.href = ADMIN_LOGIN_PATH;
                                }
                            }
                            const error = new Error('Authentication failed - invalid credentials');
                            error.name = 'AuthError';
                            (error as any).status = 401;
                            throw error;
                        }
                    } else {
                        // Not a token expiration or already refreshing - redirect to login only if not already there
                        const isLoginPage = typeof window !== 'undefined' &&
                          isAdminLoginPath(window.location.pathname);
                        
                        if (!isLoginPage) {
                            notifyAuthInvalid();
                            if (typeof window !== 'undefined') {
                                window.location.href = ADMIN_LOGIN_PATH;
                            }
                        }
                        const error = new Error('Authentication failed - invalid credentials');
                        error.name = 'AuthError';
                        (error as any).status = 401;
                        throw error;
                    }
                }
                
                // Handle other HTTP errors
                const requestId = extractRequestId(response, data);
                // Server failures carry the reference in the message so the ~60
                // call sites that surface `error.message` in a toast show it
                // without each needing to know about request ids.
                const baseMessage = data?.message || `Request failed with status ${response.status}`;
                const error = new Error(
                    requestId && response.status >= 500
                        ? `${baseMessage} (ref: ${requestId})`
                        : baseMessage
                );
                error.name = 'ApiError';
                (error as any).status = response.status;
                (error as any).responseBody = data;
                if (requestId) {
                    (error as Error & { requestId?: string }).requestId = requestId;
                }
                if (data?.profileIncomplete === true) {
                  (error as any).profileIncomplete = true;
                }
                if (typeof data?.code === 'string') {
                  (error as any).code = data.code;
                }
                throw error;
            }

            // On successful response, extend session if this is an admin endpoint
            if (
                response.ok &&
                (endpoint.includes('/admin/') || endpoint.includes('/instructor/')) &&
                !endpoint.includes('/login') &&
                !endpoint.includes('/register')
            ) {
                // Update last activity to extend session
                if (typeof window !== 'undefined') {
                    localStorage.setItem('lastActivity', Date.now().toString());
                }
            }

            return data as T;
        } catch (error) {
            if (error instanceof Error) {
                // Network errors
                if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
                    throw new Error('Network error: Unable to connect to the server');
                }
                // Re-throw API errors
                if (error.name === 'ApiError' || error.name === 'RateLimitError') {
                    throw error;
                }
            }
            // Unknown errors
            console.error('API request failed:', error);
            throw new Error('An unexpected error occurred');
        }
    };

    // Queue the request to prevent overwhelming the server
    return queueRequest(makeRequest);
}

export const authApi = {
  register: (email: string, username: string, password: string, superKey: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        token?: string;
        admin: {
          _id: string;
          accountId: string;
          email: string;
          fullName: string;
        };
      };
    }>(ADMIN_ENDPOINTS.register, {
      method: 'POST',
      body: { email, username, password, superKey },
      skipAuthRefresh: true
    }),

  login: async (email: string, password: string) => {
    // Set authentication state
    isAuthenticating = true;
    authenticationPromise = (async () => {
      try {
        const result = await apiRequest<{
          statusCode: number;
          message: string;
          data: {
            requires2FA?: boolean;
            challengeId?: string;
            maskedEmail?: string;
            expiresInMinutes?: number;
            token?: string;
            refreshToken?: string;
            expiresIn?: number;
            admin?: unknown;
          };
        }>(ADMIN_ENDPOINTS.login, {
          method: 'POST',
          body: { email, password },
          skipAuthRefresh: true // Skip auth refresh for login
        });
        
        // Clear authentication state after successful login step
        isAuthenticating = false;
        authenticationPromise = null;
        
        return result;
      } catch (error) {
        // Clear authentication state on error
        isAuthenticating = false;
        authenticationPromise = null;
        throw error;
      }
    })();
    
    return authenticationPromise;
  },

  verifyLogin2fa: (challengeId: string, code: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        token?: string;
        refreshToken?: string;
        expiresIn?: number;
        admin: unknown;
      };
    }>(ADMIN_ENDPOINTS.loginVerify2fa, {
      method: 'POST',
      body: { challengeId, code },
      skipAuthRefresh: true,
    }),

  resendLogin2faCode: (challengeId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        challengeId: string;
        maskedEmail: string;
        expiresInMinutes: number;
      };
    }>(ADMIN_ENDPOINTS.loginResendCode, {
      method: 'POST',
      body: { challengeId },
      skipAuthRefresh: true,
    }),
    
  logout: () => 
    apiRequest<{
      statusCode: number;
      message: string;
    }>(ADMIN_ENDPOINTS.logout, {
      method: 'POST',
      skipAuthRefresh: true // Skip auth refresh for logout
    }),
    
  refreshToken: (_refreshToken: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        // server-managed cookies; response may include expiresIn
        token?: string;
        refreshToken?: string;
        expiresIn?: number;
      }
    }>(ADMIN_ENDPOINTS.refresh, {
      method: 'POST',
      skipAuthRefresh: true
    }),

  sessionStatus: () =>
    apiRequest<{
      isActive: boolean;
      user?: any;
    }>(ADMIN_ENDPOINTS.sessionStatus, {
      method: 'GET',
    }),

  verify: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        authenticated: boolean;
      }
    }>(ADMIN_ENDPOINTS.verify, {
      method: 'GET',
    })
};

// Public student authentication (signup) API
export const studentPublicAuth = {
  // Student signup (public endpoint)
  register: (data: {
    email: string;
    firstName: string;
    lastName: string;
    cellPhoneNumber: string;
    password: string;
  }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        token: string;
        student: {
          _id: string;
          accountId: string;
          email: string;
          firstName: string;
          lastName: string;
          cellPhoneNumber: string;
        };
      };
    }>(`/api/auth/register`, {
      method: 'POST',
      body: data,
      // This endpoint is public; we don't need to perform admin token refresh logic for it
      skipAuthRefresh: true,
    }),
};

// Admin bulk student import API
export const adminStudentImport = {
  bulkImport: (payload: {
    importId?: string;
    users: Array<{
      firstName: string;
      lastName: string;
      dob?: string;
      phoneNumber: string | number;
      email: string;
      programName: string;
      startDate: string; // yyyy-mm-dd
      planType: 'FULL' | 'DAILY' | 'BI-WEEKLY' | 'MONTHLY';
      amount: number;
      amountPaid: number;
      amountRemaining: number;
    }>;
  }) =>
    apiRequest<{
      status: 'ok';
      importId: string | null;
      summary: { total: number; success: number; failed: number; skipped: number };
      results: Array<{
        email: string;
        studentId?: string;
        orderId?: string;
        subscriptionId?: string | null;
        status: 'created' | 'skipped' | 'failed';
        warnings?: string[];
        error?: string;
      }>;
    }>(`/api/admin/auth/students/bulk-import`, {
      method: 'POST',
      body: payload,
    }),
};

// System health and monitoring API
export const systemApi = {
  getCacheHealth: () =>
    apiRequest<{
      planCacheSize: number;
      cacheEntries: Array<{
        key: string;
        timestamp: number;
        isExpired: boolean;
      }>;
      hitRate: string;
      totalRequests: number;
      cacheHits: number;
      cacheMisses: number;
    }>('/api/auth/cache/health', { method: 'GET' })
};

// Student management API
export const studentApi = {
    getAllStudents: async (params?: {
        limit?: number;
        page?: number;
        search?: string;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        active?: boolean;
    }) => {
        const queryParams = new URLSearchParams();
        
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.search) queryParams.append('search', params.search);
        if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
        if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
        if (params?.active !== undefined) queryParams.append('active', params.active.toString());

        const queryString = queryParams.toString();
        return apiRequest<{
            statusCode: number;
            totalRecords: number;
            totalPages: number;
            currentPage: number;
            data: Array<{
                _id: string;
                accountId: string;
                email: string;
                firstName: string;
                lastName: string;
                cellPhoneNumber: string;
                isBlocked: boolean;
                isDeleted: boolean;
                lastLogin: string;
                createdAt: string;
                orders?: Array<{
                    _id: string;
                    program: string;
                    orderStatus: string;
                    paymentStatus: string;
                    createdAt: string;
                }>;
            }>;
        }>(`/api/auth/students${queryString ? `?${queryString}` : ''}`);
    },

    getStudentById: async (id: string) => {
        const params = new URLSearchParams({
            includeOrders: 'true',
            includePayments: 'true'
        });

        return apiRequest<{
            statusCode: number;
            data: {
                student: {
                    _id: string;
                    accountId: string;
                    email: string;
                    firstName: string;
                    lastName: string;
                    cellPhoneNumber: string;
                    isBlocked: boolean;
                    isDeleted: boolean;
                    accountCreated: string;
                    lastLogin: string;
                };
                courses: Array<{
                    _id: string;
                    program: string;
                    startDate: string;
                    startMonth: string;
                    deliveryMode: string;
                    batch: string;
                    status: string;
                    payment: {
                        amount: number;
                        transactionId: string;
                        type: 'ONE_TIME' | 'SUBSCRIPTION';
                        method: {
                            type: string;
                            lastFour: string;
                            expiryMonth: string;
                            expiryYear: string;
                            cardType: string;
                        };
                        date: string;
                        progress?: {
                            completedPayments: number;
                            totalPayments: number;
                            remainingPayments: number;
                            status: 'COMPLETED' | 'ACTIVE' | 'CANCELLED';
                            nextPaymentDate?: string;
                            remainingAmount?: number;
                        };
                    };
                }>;
            };
        }>(`/api/auth/students/${id}?${params}`);
    },

    /**
     * Batched overview for the User Details sidebar. Replaces the previous
     * fan-out (getStudentById + payment history + terms status + documents)
     * with a single round-trip. Per-tab heavy data is fetched lazily when
     * that tab is opened.
     */
    getStudentOverview: async (id: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
            data: {
                student: {
                    _id: string;
                    accountId: string | null;
                    email: string;
                    fullName: string | null;
                    firstName: string | null;
                    lastName: string | null;
                    cellPhoneNumber: string | null;
                    dateOfBirth: string | null;
                    programEnrolled: string | null;
                    deliveryMode: string | null;
                    graduation: { month?: string; year?: string } | null;
                    isBlocked: boolean;
                    isDeleted: boolean;
                    requiresPasswordReset: boolean;
                    lastLogin: string | null;
                    lastCertificateDownload: string | null;
                    createdAt: string | null;
                };
                enrollments: Array<{
                    _id: string;
                    program: string;
                    programName: string | null;
                    deliveryMode: string;
                    batch: string | null;
                    batchTime: string | null;
                    startDate: string | null;
                    startMonth: string | null;
                    orderStatus: string | null;
                    programStatus: string | null;
                    programPausedAt: string | null;
                    programPauseReason: string | null;
                    programCancelledAt: string | null;
                    programCancelReason: string | null;
                    paymentStatus: string | null;
                    paymentType: 'ONE_TIME' | 'SUBSCRIPTION' | null;
                    paymentAmount: number | null;
                    enrollmentDate: string | null;
                    createdAt: string | null;
                    paymentAuthorization: {
                        status: 'not_required' | 'pending' | 'completed';
                        triggerPaymentId: string | null;
                        completedAt: string | null;
                        context: 'new-enrollment' | 'card-changed' | null;
                        cardholderType: 'self' | 'third-party' | null;
                        signedAt: string | null;
                    };
                }>;
                paymentAuthorizationSummary: {
                    pendingCount: number;
                    completedCount: number;
                    notRequiredCount: number;
                };
                paymentSummary: {
                    totalPayments: number;
                    completedCount: number;
                    failedCount: number;
                    pendingCount: number;
                    refundedCount: number;
                    totalProgramAmount: number;
                    totalPaid: number;
                    totalOutstanding: number;
                    activeSubscriptions: number;
                    totalSubscriptions: number;
                    successRate: number | null;
                };
                documentsSummary: {
                    total: number;
                    uploaded: number;
                    required: number;
                    optional: number;
                    approved: number;
                    rejected: number;
                    pendingReview: number;
                    missingRequired: number;
                };
                complianceSummary: {
                    hasActiveSignature: boolean;
                    termsVersion: string | null;
                    signedAt: string | null;
                    dataProcessingConsent: boolean | null;
                    marketingConsent: boolean | null;
                    thirdPartySharingConsent: boolean | null;
                    totalAgreements: number;
                };
            };
        }>(`/api/admin/users/students/${id}/overview`);
    },

    updateStudent: async (id: string, data: {
        fullName?: string;
        cellPhoneNumber?: string;
        dateOfBirth?: string;
        programEnrolled?: string | null;
        deliveryMode?: string;
        graduation?: {
            month?: string;
            year?: string;
        };
        isBlocked?: boolean;
        requiresPasswordReset?: boolean;
    }) => {
        return apiRequest<{
            success: boolean;
            message: string;
            data: {
                _id: string;
                type: string;
                accountId: string;
                email: string;
                fullName: string;
                cellPhoneNumber: string;
                dateOfBirth?: string;
                programEnrolled?: string | null;
                deliveryMode?: string;
                graduation?: {
                    month: string;
                    year: string;
                };
                isBlocked: boolean;
                isDeleted: boolean;
                requiresPasswordReset?: boolean;
                createdAt: string;
                updatedAt: string;
                lastLogin?: string | null;
                lastCertificateDownload?: string | null;
                certification?: any;
            };
        }>(`/api/admin/users/student/${id}`, {
            method: 'PUT',
            body: data
        });
    },

    resendCredentials: async (studentId: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
            data: {
                studentId: string;
                email: string;
                requiresPasswordReset: boolean;
            };
        }>(`/api/admin/users/students/${studentId}/resend-credentials`, {
            method: 'POST',
        });
    },

    impersonate: async (studentId: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
            data: {
                impersonationUrl: string;
                expiresAt: string;
            };
        }>(`/api/admin/users/students/${studentId}/impersonate`, {
            method: 'POST',
        });
    },

    deleteStudent: async (id: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
        }>(`/api/auth/students/${id}`, {
            method: 'DELETE'
        });
    },

    blockStudent: async (id: string, reason: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
        }>(`/api/auth/students/${id}/block`, {
            method: 'POST',
            body: { reason }
        });
    },

    unblockStudent: async (id: string) => {
        return apiRequest<{
            statusCode: number;
            message: string;
        }>(`/api/auth/students/${id}/unblock`, {
            method: 'POST'
        });
    },

    changeStudentEmail: async (studentId: string, newEmail: string) => {
        return apiRequest<{
            success: boolean;
            message: string;
            data: {
                studentId: string;
                oldEmail: string;
                newEmail: string;
            };
        }>(`/api/admin/users/students/${studentId}/change-email`, {
            method: 'POST',
            body: { newEmail }
        });
    }
};

// API Response Types
type ApiResponse<T> = {
  success: boolean;
  message?: string;
  data?: T;
} & T;

type CategoryResponse = {
  category: {
    _id: string;
    catagoryId?: string;
    title: string;
    field?: string;
    description?: string;
    navOrder?: number;
    showInNav?: boolean;
    navImageUrl?: string | null;
  };
};

type CategoriesResponse = {
  categories: Array<{
    _id: string;
    catagoryId?: string;
    title: string;
    field?: string;
    description?: string;
    navOrder?: number;
    showInNav?: boolean;
    navImageUrl?: string | null;
  }>;
};

type ProgramResponse = {
  program: {
    _id: string;
    programId?: string;
    title: string;
    description: string;
    status?: string;
    category?: {
      _id: string;
      title: string;
    };
    programType?: string;
    createdAt?: string;
  };
};

type ProgramsResponse = {
  success?: boolean;
  programs?: Program[];
  error?: string;
  // Alternative response structures
  data?: {
    programs?: Program[];
    pagination?: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
  statusCode?: number;
  message?: string;
};

export interface ProgramDraft {
  draftId: string;
  programData: any;
  lastModified: string;
  version: number;
  sharedWith: string[];
  createdBy: {
    id: string;
    name: string;
  };
}

// Add this near the top of the file with other types
export type Program = {
  _id: string;
  programId?: string;
  title: string;
  subtitle?: string;
  description: string;
  videoUrl?: string;
  language?: string;
  status?: string;
  category?: {
    _id: string;
    title: string;
    description?: string;
    categoryId?: string;
    field?: string;
  };
  programType?: string;
  createdAt?: string;
  updatedAt?: string;
  keyFeatures?: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    descriptionHeading?: string;
    description?: string;
  }>;
  // New module-related fields
  payAsYouGo?: boolean;
  moduleConfiguration?: {
    payAsYouGo: boolean;
    // ❌ Remove modulePricingEnabled - it's no longer required by the backend
    modules: Array<{
      _id?: string;
      name: string;
      description?: string;
      programId?: string;
      price: number;
      isActive: boolean;
      order: number;
      metadata?: {
        difficulty?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
        tags?: string[];
      };
      prerequisites?: string[];
      estimatedCompletionTime?: number;
      isExam?: boolean;
      examDetails?: {
        passingScore: number;
        timeLimit?: number;
        questions?: number;
      };
    }>;
    pricingStrategy?: 'INDIVIDUAL' | 'BUNDLE';
    bundleDiscount?: number;
    minimumModulesForBundle?: number;
  };
  modulePricingMetadata?: {
    totalModules: number;
    totalPrice: number;
    averageModulePrice: number;
    bundlePrice?: number;
    savingsWithBundle?: number;
  };
};

// Program management API
export const programApi = {
  // Create a new program
  addProgram: async (programData: any) => {
    try {
      const response = await apiRequest<{
        statusCode: number;
        message: string;
        data: {
          programId: string;
          title: string;
          subtitle: string;
          description: string;
          language: string;
          status: string;
          createdAt: string;
          updatedAt: string;
        };
      }>('/api/programs/admin', {
      method: 'POST',
      body: programData
      });
      return response;
    } catch (error) {
      console.error('Failed to create program:', error);
      throw error;
    }
  },

  // Get all programs (admin view)
  getAllPrograms: async (params?: {
    categoryId?: string;
    programType?: string;
    status?: string;
    language?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value) queryParams.append(key, value.toString());
        });
      }
      
      const response = await apiRequest<{
        statusCode: number;
        message: string;
        data: {
          programs: any[];
          pagination: {
            total: number;
            page: number;
            limit: number;
            pages: number;
          };
        };
      }>(`/api/programs/admin?${queryParams}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch programs:', error);
      throw error;
    }
  },

  // Get a single program by ID
  getProgramById: async (id: string) => {
    try {
      const response = await apiRequest<{
        statusCode: number;
        message: string;
        data: any;
      }>(`/api/programs/admin/${id}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch program:', error);
      throw error;
    }
  },

  // Update an existing program
  updateProgram: async (id: string, programData: any) => {
    try {
      const response = await apiRequest<{
        statusCode: number;
        message: string;
        data: any;
      }>(`/api/programs/admin/${id}`, {
        method: 'PUT',
        body: programData
      });
      return response;
    } catch (error) {
      console.error('Failed to update program:', error);
      throw error;
    }
  },

  // Delete a program
  deleteProgram: async (id: string) => {
    try {
      const response = await apiRequest<{
        statusCode: number;
        message: string;
        data: { id: string };
      }>(`/api/programs/admin/${id}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Failed to delete program:', error);
      throw error;
    }
  },

  // Get all drafts
  getDrafts: () => 
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        drafts: ProgramDraft[];
      };
    }>('/api/programs/drafts'),

  // Save a draft
  saveDraft: (data: any, draftId?: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        draft: ProgramDraft;
      };
    }>(draftId ? `/api/programs/drafts/${draftId}` : '/api/programs/drafts', {
      method: draftId ? 'PUT' : 'POST',
      body: data
    }),

  // Delete a draft
  deleteDraft: (draftId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
    }>(`/api/programs/drafts/${draftId}`, {
      method: 'DELETE'
    }),

  // Share a draft
  shareDraft: (draftId: string, adminEmails: string[]) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        draft: ProgramDraft;
      };
    }>(`/api/programs/drafts/${draftId}/share`, {
      method: 'POST',
      body: { adminEmails }
    })
};

// Category management API
export const categoryApi = {
  getAllCategories: async (): Promise<ApiResponse<CategoriesResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<CategoriesResponse>>('/api/addcategory');
      return response;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return { success: false, categories: [] };
    }
  },

  /** List categories for admin filters (e.g. programs-v2). */
  getCategoriesList: async (): Promise<ApiResponse<CategoriesResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<CategoriesResponse>>('/api/categories');
      return response;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      return { success: false, categories: [] };
    }
  },

  getCategoryById: async (id: string): Promise<ApiResponse<CategoryResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<CategoryResponse>>(`/api/addcategory/${id}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch category:', error);
      return { 
        success: false, 
        category: {
          _id: '',
          title: '',
          description: ''
        }
      };
    }
  },

  createCategory: async (category: {
    title: string;
    field: string;
    description?: string;
    navOrder?: number;
    showInNav?: boolean;
    navImageUrl?: string | null;
  }): Promise<ApiResponse<CategoryResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<CategoryResponse>>('/api/addcategory', {
        method: 'POST',
        body: category
      });
      return response;
    } catch (error) {
      console.error('Failed to create category:', error);
      return { 
        success: false, 
        category: {
          _id: '',
          title: '',
          description: ''
        }
      };
    }
  },

  updateCategory: async (
    id: string,
    updates: {
      title?: string;
      field?: string;
      description?: string;
      navOrder?: number;
      showInNav?: boolean;
      navImageUrl?: string | null;
    }
  ): Promise<ApiResponse<CategoryResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<CategoryResponse>>(`/api/addcategory/${id}`, {
        method: 'PUT',
        body: updates
      });
      return response;
    } catch (error) {
      console.error('Failed to update category:', error);
      return { 
        success: false, 
        category: {
          _id: '',
          title: '',
          description: ''
        }
      };
    }
  },

  getCategoryNavImageUploadUrl: async (params: {
    fileName: string;
    fileType: string;
    fileSize?: number;
  }): Promise<
    ApiResponse<{
      data?: { uploadUrl: string; imageUrl: string; expiresIn: number };
    }>
  > => {
    return apiRequest("/api/categories/nav-image/upload-url", {
      method: "POST",
      body: params,
    });
  },

  uploadCategoryNavImage: async (file: File): Promise<string> => {
    validateNavImageFile(file);

    const response = await categoryApi.getCategoryNavImageUploadUrl({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    });

    const data = response.data;
    if (!response.success || !data?.uploadUrl || !data?.imageUrl) {
      throw new Error(
        (response as { message?: string }).message || "Failed to get upload URL",
      );
    }

    await putFileToPresignedUrl(data.uploadUrl, file);
    return data.imageUrl;
  },

  deleteCategory: async (id: string): Promise<ApiResponse<{ id: string }>> => {
    try {
      // Ensure we're not sending any body data for DELETE request
      const response = await apiRequest<ApiResponse<{ id: string }>>(`/api/addcategory/${id}`, {
        method: 'DELETE',
        // Explicitly ensure no body is sent
        body: undefined
      });
      return response;
    } catch (error) {
      console.error('Failed to delete category:', error);
      // Check if it's a validation error from backend
      if (error instanceof Error && error.message && error.message.includes('validation')) {
        return { 
          success: false, 
          id: '', 
          message: 'Backend validation error: DELETE requests should not validate body fields. Please contact the backend team to fix the validation middleware.' 
        };
      }
      return { success: false, id: '', message: error instanceof Error ? error.message : 'Failed to delete category' };
    }
  },

  // Program-related functions
  getCategoryPrograms: async (categoryId: string): Promise<ApiResponse<ProgramsResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<ProgramsResponse>>(`/api/programs/category/${categoryId}`);
      return response;
    } catch (error) {
      console.error('Failed to fetch category programs:', error);
      return { success: false, programs: [] };
    }
  },

  createProgram: async (program: {
    title: string;
    description: string;
    categoryId: string;
    programType?: string;
    status?: string;
  }): Promise<ApiResponse<ProgramResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<ProgramResponse>>('/api/programs/admin', {
        method: 'POST',
        body: program
      });
      return response;
    } catch (error) {
      console.error('Failed to create program:', error);
      return { 
        success: false, 
        program: {
          _id: '',
          title: '',
          description: ''
        }
      };
    }
  },


  updateProgram: async (id: string, updates: {
    title?: string;
    description?: string;
    programType?: string;
    status?: string;
  }): Promise<ApiResponse<ProgramResponse>> => {
    try {
      const response = await apiRequest<ApiResponse<ProgramResponse>>(`/api/programs/admin/${id}`, {
        method: 'PUT',
        body: updates
      });
      return response;
    } catch (error) {
      console.error('Failed to update program:', error);
      return { 
        success: false, 
        program: {
          _id: '',
          title: '',
          description: ''
        }
      };
    }
  },

  deleteProgram: async (id: string): Promise<ApiResponse<{ id: string }>> => {
    try {
      const response = await apiRequest<ApiResponse<{ id: string }>>(`/api/programs/admin/${id}`, {
        method: 'DELETE'
      });
      return response;
    } catch (error) {
      console.error('Failed to delete program:', error);
      return { success: false, id: '' };
    }
  }
};

// Subscription management API
export const subscriptionApi = {
  // Get all plans
  getPlans: (includeInactive?: boolean) =>
    apiRequest<{ success: boolean; plans: any[] }>(`/api/subscription/plan${includeInactive ? '?includeInactive=true' : ''}`, { 
      method: 'GET' 
    }),
  
  // Create a new plan
  createPlan: (plan: {
    displayName: string;
    amount: number;
    planType: "FULL_PAY" | "DAILY" | "BI_WEEKLY" | "MONTHLY";
    numberOfPayments?: number;
    programId?: string;
  }) =>
    apiRequest<{ message: string; plan: any }>('/api/subscription/plan', { 
      method: 'POST', 
      body: plan 
    }),
  
  // Update an existing plan
  updatePlan: (planId: string, updates: {
    displayName?: string;
    amount?: number;
    planType?: "FULL_PAY" | "BI_WEEKLY" | "MONTHLY";
    numberOfPayments?: number;
  }) =>
    apiRequest<{ success: boolean; message: string; plan: any }>(`/api/subscription/plan/${planId}`, { 
      method: 'PUT', 
      body: updates 
    }),
  
  // Delete a plan (mark as inactive)
  deletePlan: (planId: string) =>
    apiRequest<{ success: boolean; message: string; plan: any }>(`/api/subscription/plan/${planId}`, { 
      method: 'DELETE' 
    }),
  
  // Create a subscription
  createSubscription: (data: {
    email: string;
    cardNonce: string;
    planId: string;
    postalCode: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    studentId: string;
  }) =>
    apiRequest<{ success: boolean; message: string; data: any }>('/api/subscription/create', {
      method: 'POST',
      body: data
    }),
  
  // Process one-time payment for FULL_PAY plans
  processOneTimePayment: (data: {
    planId?: string;
    planSlug?: string;
    cardNonce: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }) =>
    apiRequest<{ success: boolean; message: string; payment: any }>('/api/subscription/one-time/checkout', {
      method: 'POST',
      body: data
    }),
  
  // Recalculate payment schedule for a subscription
  recalculatePayments: (subscriptionId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        success: boolean;
        subscription: any;
        recalculatedPayments: number;
      };
    }>(`/api/admin/auth/subscriptions/${subscriptionId}/recalculate-payments`, {
      method: 'POST'
    }),
  
  // Reconcile payment counts for a student
  reconcileStudentPayments: (studentId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        success: boolean;
        reconciledSubscriptions: number;
        fixedPayments: number;
      };
    }>(`/api/admin/auth/subscriptions/reconcile/${studentId}`, {
      method: 'POST'
    }),

  // Freeze subscription
  freezeSubscription: (subscriptionId: string, data?: { 
    reason?: string; 
    resumeDate?: string;
  }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        pausedAt: string | null;
        resumeDate: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/freeze`,
      { method: 'POST', body: data || {} }
    ),

  // Unfreeze subscription  
  unfreezeSubscription: (subscriptionId: string, resumeDate: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        nextPaymentDate: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/unfreeze`,
      { method: 'POST', body: { resumeDate } }
    ),

  // Retry payment (direct payment ID)
  retryPayment: (paymentId: string, paymentMethodId?: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        originalPaymentId: string;
        newPaymentId: string;
        amount: number;
        status: string;
        retryAttempt: number;
      };
    }>(
      `/api/admin/payment-consistency/payments/${paymentId}/retry`,
      { 
        method: 'POST', 
        body: paymentMethodId ? { paymentMethodId } : {} 
      }
    ),

  // Retry subscription payment (finds latest failed payment)
  retrySubscriptionPayment: (subscriptionId: string, paymentMethodId?: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        originalPaymentId: string;
        newPaymentId: string;
        amount: number;
        status: string;
        retryAttempt: number;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/retry-payment`,
      { 
        method: 'POST', 
        body: paymentMethodId ? { paymentMethodId } : {} 
      }
    ),

  // Pause subscription
  pauseSubscription: (subscriptionId: string, data?: { reason?: string; resumeDate?: string }) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        pausedAt: string | null;
        resumeDate: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/pause`,
      { method: 'POST', body: data || {} }
    ),

  // Resume subscription
  resumeSubscription: (subscriptionId: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        nextPaymentDate: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/resume`,
      { method: 'POST' }
    ),

  // Cancel subscription
  cancelSubscription: (subscriptionId: string, reason?: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        canceledAt: string;
        cancelReason: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/cancel`,
      { method: 'POST', body: reason ? { reason } : {} }
    ),

  // Block subscription
  blockSubscription: (subscriptionId: string, reason?: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
        blockedAt: string;
        blockReason: string | null;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/block`,
      { method: 'POST', body: reason ? { reason } : {} }
    ),

  // Unblock subscription
  unblockSubscription: (subscriptionId: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        status: string;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/unblock`,
      { method: 'POST' }
    ),

  // Update payment method for subscription
  updateSubscriptionPaymentMethod: (subscriptionId: string, paymentMethodId: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        subscriptionId: string;
        paymentMethodId: string;
        updatedAt: string;
      };
    }>(
      `/api/admin/subscriptions/${subscriptionId}/update-payment-method`,
      { method: 'POST', body: { paymentMethodId } }
    ),

  /** Lookup setup-pending enrollments (orphan Apple Pay / missing Square sub). */
  getSetupPending: (params: { email?: string; studentId?: string }) => {
    const search = new URLSearchParams();
    if (params.email) search.set("email", params.email);
    if (params.studentId) search.set("studentId", params.studentId);
    const qs = search.toString();
    return apiRequest<{
      success: boolean;
      data: {
        student: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          phone: string | null;
          squareCustomerId: string | null;
        };
        enrollments: Array<{
          localSubscriptionId: string;
          orderId: string | null;
          programName: string;
          programId: string;
          planId: string;
          planName: string;
          cadence: string;
          deliveryMode: string;
          amountPerInstallment: number;
          numberOfPayments: number;
          completedPayments: number;
          remainingPayments: number;
          amountPaid: number;
          amountRemaining: number;
          status: string;
          squareCustomerId: string | null;
          squareSubscriptionId: string | null;
          firstInstallmentPaymentId: string | null;
          suggestedStartDate: string;
          nextPaymentDate: string | null;
          setupPending: boolean;
        }>;
        paymentMethods: Array<{
          id: string;
          squarePaymentMethodId: string;
          squareCustomerId: string | null;
          cardType: string;
          lastFour: string;
          expiryDate: string;
          cardBrand: string;
          isDefault: boolean;
          status: string;
        }>;
      };
    }>(`/api/admin/subscriptions/setup-pending${qs ? `?${qs}` : ""}`);
  },

  /** Create Square subscription from a student-saved card and link local sub. */
  completeSquareSetup: (
    subscriptionId: string,
    data: { paymentMethodId: string; startDate: string }
  ) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        localSubscriptionId: string;
        squareSubscriptionId: string;
        startDate: string;
        customerId: string;
        cardId: string;
        orderId: string | null;
      };
    }>(`/api/admin/subscriptions/${subscriptionId}/complete-square-setup`, {
      method: "POST",
      body: data,
    }),
};

// Payment Authorization types
export type AuthorizationSignature = {
  method: 'drawn' | 'uploaded';
  dataUrl?: string;
  fileUrl?: string;
};

export type StudentPaymentAuthorization = {
  _id?: string;
  studentName: string;
  address: string;
  phone: string;
  photoIdUrl: string;
  programEnrolled: string;
  cardName: string;
  last4: string;
  amountPaid: number;
  educationPartners?: string;
  requiredForm?: boolean;
  selectedPlan?: string;
  autoPayPlan?: boolean;
  paidInFull?: boolean;
  autoPayAmount?: number;
  oneTimePaymentAmount?: number;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingZipcode?: string;
  cardType?: string;
  amountOfCharge?: number;
  cardNumberMasked?: string;
  expMonth?: string;
  expYear?: string;
  onPaymentPlan: boolean;
  paymentPlanNote?: string;
  paymentAuthorizationType: 'one-time' | 'plan';
  context: 'new-enrollment' | 'card-change';
  consentTextVersion: string;
  signature: AuthorizationSignature;
  signedAt: string;
  pdfUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
};

export type ThirdPartyPaymentAuthorization = {
  _id?: string;
  studentName: string;
  programEnrolled: string;
  cardHolderName: string;
  cardHolderPhotoIdUrl: string;
  cardHolderAddress: string;
  cardHolderPhone: string;
  last4: string;
  amountPaid: number;
  educationPartners?: string;
  requiredForm?: boolean;
  selectedPlan?: string;
  autoPayPlan?: boolean;
  paidInFullOverride?: boolean;
  autoPayAmount?: number;
  oneTimePaymentAmount?: number;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingZipcode?: string;
  cardType?: string;
  amountOfCharge?: number;
  cardNumberMasked?: string;
  expMonth?: string;
  expYear?: string;
  onPaymentPlan: boolean;
  consentTextVersion: string;
  signature: AuthorizationSignature;
  signedAt: string;
  pdfUrl?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
};

type AdminAuthorizationListResponse<T> = {
  success: boolean;
  count: number;
  data: T[];
};

// Payment Authorization API (admin GET endpoints only)
export const paymentAuthorizationApi = {
  // Admin: list student-as-cardholder authorizations
  getStudentAuthorizations: (query?: Record<string, string | number | boolean>) => {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<AdminAuthorizationListResponse<StudentPaymentAuthorization>>(`/api/payment-authorization/admin/student${qs ? `?${qs}` : ''}`);
  },

  // Admin: list third-party authorizations
  getThirdPartyAuthorizations: (query?: Record<string, string | number | boolean>) => {
    const params = new URLSearchParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<AdminAuthorizationListResponse<ThirdPartyPaymentAuthorization>>(`/api/payment-authorization/admin/third-party${qs ? `?${qs}` : ''}`);
  }
};

// Helper functions that use the API - Updated to match new API structure

export const paymentApi = {
  getPaymentHistory: (params: {
    studentId?: string;
    orderId?: string;
    transactionId?: string;
    status?: string;
    type?: string;
    fromDate?: string;
    toDate?: string;
    limit?: number;
    page?: number;
    search?: string;
    includeSquareData?: boolean;
    include?: string;
  } = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.studentId) queryParams.append('studentId', params.studentId);
    if (params.orderId) queryParams.append('orderId', params.orderId);
    if (params.transactionId) queryParams.append('transactionId', params.transactionId);
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.fromDate) queryParams.append('fromDate', params.fromDate);
    if (params.toDate) queryParams.append('toDate', params.toDate);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.includeSquareData) queryParams.append('includeSquareData', 'true');
    if (params.include) queryParams.append('include', params.include);

    const queryString = queryParams.toString();
    return apiRequest<{
      success: boolean;
      data: {
        payments: Array<{
          _id: string;
          orderId: string;
          studentId: string;
          transactionId: string;
          amount: number;
          type: 'PAYMENT' | 'REFUND' | 'CHARGEBACK';
          status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
          paymentMethod?: {
            lastFour: string;
            cardType: string;
            expiryMonth?: number;
            expiryYear?: number;
          };
          timestamp: string;
          order?: {
            program: string;
            deliveryMode: string;
            paymentType: string;
          };
          student?: {
            fullName: string;
            email: string;
            accountId: string;
          };
          subscription?: {
            squareSubscriptionId: string;
            numberOfPayments: number;
            completedPayments: number;
            status: string;
          };
          squareData?: {
            type: string;
            data: any;
          };
        }>;
        pagination: {
          currentPage: number;
          totalPages: number;
          totalItems: number;
          itemsPerPage: number;
        };
        analytics?: {
          summary: {
            totalPayments: number;
            totalAmount: number;
            completedPayments: number;
            failedPayments: number;
            successRate: string;
          };
          byStatus: Record<string, number>;
          byType: Record<string, number>;
        };
        summary?: {
          student: {
            _id: string;
            fullName: string;
            email: string;
            accountId: string;
          };
          activeSubscriptions: number;
          totalOutstanding: number;
          totalPaid: number;
          totalRemaining: number;
        };
        upcoming?: {
          total: number;
          totalAmount: number;
          next30Days: number;
          payments: Array<{
            date: string;
            amount: number;
            subscriptionId: string;
            status: string;
          }>;
        };
      };
    }>(`/api/admin/auth/payments${queryString ? `?${queryString}` : ''}`);
  },
  
  // Get orders with payment issues
  getOrdersWithPaymentIssues: (params: {
    limit?: number;
    page?: number;
    severity?: 'high' | 'medium' | 'low';
  } = {}) => {
    const queryParams = new URLSearchParams();
    
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.severity) queryParams.append('severity', params.severity);

    const queryString = queryParams.toString();
    return apiRequest<{
      success: boolean;
      totalRecords: number;
      orders: Array<{
        _id: string;
        program: string;
        paymentStatus: string;
        paymentTransactionId: string;
        paymentAmount: number;
        orderStatus: string;
        severity?: 'high' | 'medium' | 'low';
        student: string | {
          _id: string;
          firstName: string;
          lastName: string;
          email: string;
          accountId?: string;
        };
        createdAt?: string;
        needsPaymentReview?: boolean;
        hasPaymentHistory?: boolean;
        issueType?: 'payment_failed' | 'flagged_for_review' | 'payment_timeout' | 'status_mismatch';
      }>;
      summary: {
        highSeverity: number;
        mediumSeverity: number;
        lowSeverity: number;
      };
      // Also support nested structure for backwards compatibility
      statusCode?: number;
      message?: string;
      data?: {
        success: boolean;
        totalRecords: number;
        page?: number;
        limit?: number;
        totalPages?: number;
        orders: Array<{
          _id: string;
          program: string;
          paymentStatus: string;
          paymentTransactionId: string;
          paymentAmount: number;
          orderStatus: string;
          createdAt: string;
          needsPaymentReview: boolean;
          hasPaymentHistory: boolean;
          issueType: 'payment_failed' | 'flagged_for_review' | 'payment_timeout' | 'status_mismatch';
          severity: 'high' | 'medium' | 'low';
          student: {
            _id: string;
            firstName: string;
            lastName: string;
            email: string;
            accountId: string;
          };
        }>;
        summary: {
          highSeverity: number;
          mediumSeverity: number;
          lowSeverity: number;
        };
      };
    }>(`/api/admin/auth/payments/issues${queryString ? `?${queryString}` : ''}`);
  },
  
  // Manually trigger payment reconciliation
  getPaymentLedger: (params: {
    studentId?: string;
    email?: string;
    accountId?: string;
    status?: string;
    type?: string;
    fromDate?: string;
    toDate?: string;
    search?: string;
    includeDue?: boolean;
    limit?: number;
    page?: number;
  } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.studentId) queryParams.append("studentId", params.studentId);
    if (params.email) queryParams.append("email", params.email);
    if (params.accountId) queryParams.append("accountId", params.accountId);
    if (params.status) queryParams.append("status", params.status);
    if (params.type) queryParams.append("type", params.type);
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);
    if (params.search) queryParams.append("search", params.search);
    if (params.includeDue === false) queryParams.append("includeDue", "false");
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.page) queryParams.append("page", params.page.toString());
    const queryString = queryParams.toString();
    return apiRequest<{
      success: boolean;
      data: {
        rows: Array<{
          id: string;
          source: "payment_history" | "square_due";
          status: "PAID" | "FAILED" | "PENDING" | "REFUNDED" | "DUE";
          category: string;
          amount: number;
          currency: string;
          paidAt: string | null;
          dueDate: string | null;
          scheduledDate: string | null;
          transactionId: string | null;
          squarePaymentId: string | null;
          squareSubscriptionId: string | null;
          squareInvoiceId: string | null;
          orderId: string | null;
          subscriptionId: string | null;
          studentId: string | null;
          studentName: string | null;
          studentEmail: string | null;
          accountId: string | null;
          programName: string | null;
          deliveryMode: string | null;
          paymentType: string | null;
          paymentMethod: {
            type?: string;
            lastFour?: string;
            cardType?: string;
          } | null;
          installmentNumber: number | null;
          mismatch: boolean;
          type: "PAYMENT" | "REFUND" | "CHARGEBACK" | "DELAY_FEE";
        }>;
        pagination: {
          currentPage: number;
          totalPages: number;
          totalItems: number;
          itemsPerPage: number;
        };
      };
    }>(`/api/admin/auth/payments/ledger${queryString ? `?${queryString}` : ""}`);
  },

  syncPaymentsFromSquare: (body: {
    dryRun?: boolean;
    studentId?: string;
    email?: string;
    accountId?: string;
  } = {}) =>
    apiRequest<{
      success: boolean;
      message: string;
      data: {
        dryRun: boolean;
        scanned: number;
        eligible: number;
        updated: number;
        mismatches: number;
        results: Array<{
          subscriptionId: string;
          squareSubscriptionId: string;
          studentId: string;
          previousCompletedPayments: number;
          newCompletedPayments: number;
          mismatch: boolean;
          applied: boolean;
          message: string;
        }>;
      };
    }>("/api/admin/auth/payments/sync-from-square", {
      method: "POST",
      body,
    }),

  reconcilePayments: () =>
    apiRequest<{
      success: boolean;
      results: {
        ordersWithoutPayments: number;
        paymentsWithoutOrders: number;
        ordersFixed: number;
        paymentsWithOrdersCreated: number;
      };
      timestamp: string;
      // Also support nested structure for backwards compatibility
      statusCode?: number;
      message?: string;
      data?: {
        success: boolean;
        results: {
          ordersWithoutPayments: number;
          paymentsWithoutOrders: number;
          ordersFixed: number;
          paymentsWithOrdersCreated: number;
        };
        timestamp: string;
      };
    }>('/api/admin/auth/payments/reconcile', { method: 'GET' }),
  
  // Update order status
  updateOrderStatus: (orderId: string, orderStatus: string, reason: string) =>
    apiRequest<{
      success: boolean;
      message: string;
      order: any;
    }>(`/api/admin/orders/${orderId}/status`, {
      method: 'PATCH',
      body: { orderStatus, reason }
    }),

  // Register webhook endpoints for Square integration
  registerSquareWebhooks: (callbackUrl: string, events: string[]) =>
    apiRequest<{
      success: boolean;
      webhookSubscription: any;
    }>('/api/admin/webhooks/register', {
      method: 'POST',
      body: { callbackUrl, events }
    }),

  // Get webhook configurations
  getWebhookConfigurations: () =>
    apiRequest<{
      success: boolean;
      webhooks: any[];
    }>('/api/admin/auth/webhooks/config', { method: 'GET' }),
    
  // Test webhook handling
  testWebhook: (eventType: string, payload: any) =>
    apiRequest<{
      success: boolean;
      result: any;
    }>('/api/admin/webhooks/test', {
      method: 'POST',
      body: { eventType, payload }
    }),

  // Hard delete a payment record (admin only with special privileges)
  permanentlyDeletePayment: (paymentId: string, adminConfirmation: boolean) =>
    apiRequest<{
      success: boolean;
      message: string;
    }>(`/api/admin/payment/${paymentId}/permanent`, {
      method: 'DELETE',
      headers: {
        'X-Admin-Confirmation': adminConfirmation ? 'true' : 'false'
      }
    }),

  getStudentPaymentAnalytics: async (studentId: string) => {
    return apiRequest<{
      statusCode: number;
      data: {
        summary: {
          totalPaid: number;
          totalDue: number;
          remainingBalance: number;
          lastPaymentDate: string;
          nextPaymentDate: string | null;
        };
        paymentTimeline: Array<{
          date: string;
          amount: number;
          type: 'payment' | 'refund' | 'due';
        }>;
        paymentMethods: Array<{
          type: string;
          lastFour: string;
          cardType: string;
          isDefault: boolean;
          expiryMonth: string;
          expiryYear: string;
        }>;
        subscriptionSummary: {
          activeSubscriptions: number;
          totalSubscriptionAmount: number;
          nextBillingTotal: number;
        };
      };
    }>(`/api/admin/students/${studentId}/payment-analytics`);
  },

  getPaymentMethodDetails: async (studentId: string, paymentMethodId: string) => {
    return apiRequest<{
      statusCode: number;
      data: {
        _id: string;
        type: string;
        lastFour: string;
        cardType: string;
        expiryMonth: string;
        expiryYear: string;
        isDefault: boolean;
        billingAddress?: {
          line1: string;
          line2?: string;
          city: string;
          state: string;
          postalCode: string;
          country: string;
        };
        transactions: Array<{
          _id: string;
          amount: number;
          status: string;
          date: string;
          type: string;
        }>;
      };
    }>(`/api/admin/students/${studentId}/payment-methods/${paymentMethodId}`);
  },

  updatePaymentMethod: async (
    studentId: string, 
    paymentMethodId: string, 
    updates: {
      isDefault?: boolean;
      expiryMonth?: string;
      expiryYear?: string;
      billingAddress?: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
      };
    }
  ) => {
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        _id: string;
        isDefault: boolean;
        expiryMonth: string;
        expiryYear: string;
        updatedAt: string;
      };
    }>(`/api/admin/students/${studentId}/payment-methods/${paymentMethodId}`, {
      method: 'PUT',
      body: updates
    });
  },

  removePaymentMethod: async (studentId: string, paymentMethodId: string) => {
    return apiRequest<{
      statusCode: number;
      message: string;
    }>(`/api/admin/students/${studentId}/payment-methods/${paymentMethodId}`, {
      method: 'DELETE'
    });
  }
};

// Updated program management functions to match new API structure
export async function fetchAllPrograms(params?: {
  categoryId?: string;
  programType?: string;
  status?: string;
  language?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<ProgramsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params?.categoryId) queryParams.append('categoryId', params.categoryId);
  if (params?.programType) queryParams.append('programType', params.programType);
  if (params?.status) queryParams.append('status', params.status);
  if (params?.language) queryParams.append('language', params.language);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());

  const queryString = queryParams.toString();
  const endpoint = `/api/programs/admin${queryString ? `?${queryString}` : ''}`;
  
  try {
    const response = await apiRequest<any>(endpoint, {
      method: 'GET'
    });
    
    return response;
  } catch (error) {
    console.error('Error fetching programs:', error);
    throw error;
  }
}

export async function fetchProgramById(id: string) {
  return apiRequest<{
    success?: boolean;
    statusCode?: number;
    message: string;
    data?: {
      // Program data is directly in data, not nested under 'program'
      _id: string;
      programId: string;
      title: string;
      subtitle?: string;
      description?: string;
      categoryId?: string;
      status: string;
      programType?: string;
      defaultProgramType?: string;
      tagline?: string;
      socialMedia?: any;
      about?: string;
      videoUrl?: string;
      language?: string;
      modules?: Array<{
        _id?: string;
        id?: string;
        name?: string;
        label?: string;
        price?: number;
        isActive?: boolean;
        order?: number;
      }>;
      keyFeatures?: any[];
      thisCourseData?: any[];
      whoisthisFor?: any[];
      wherecaniWork?: any[];
      clinicalProcedures?: any[];
      nationalCertifications?: any;
      workPlaces?: string[];
      faqs?: any[];
      podcastData?: any[];
      breadcrumb?: any[];
      programTypes?: any[];
      programComparison?: any;
      typeSpecificData?: any;
      premiumDescription?: string;
      premiumRating?: number;
      premiumReviewCount?: number;
      premiumStudentCount?: number;
      premiumViewCount?: number;
      premiumLikeCount?: number;
      premiumShareCount?: number;
      payAsYouGo?: boolean;
      moduleCount?: number;
      moduleConfiguration?: any;
      modulePricingMetadata?: any;
      createdAt?: string;
      updatedAt?: string;
    };
    errors?: Array<{
      code: string;
      expected?: any;
      received?: any;
      path: string[];
      message: string;
    }>;
  }>(`/api/programs/admin/${id}`, {
    method: 'GET'
  });
}

export async function createProgram(programData: any) {
  return apiRequest<{
    success?: boolean;
    statusCode?: number;
    message: string;
    data?: {
      program: {
        id: string;
        programId: string;
        title: string;
        status: string;
        payAsYouGo?: boolean;
        moduleCount?: number;
        baseModulesCount?: number;
        moduleConfigurationCount?: number;
        unifiedModulesCount?: number;
        createdAt: string;
      };
      modules: Array<{
        id: string;
        label: string;
        price: number;
        isActive: boolean;
        order: number;
      }>;
    };
    errors?: Array<{
      code: string;
      expected?: any;
      received?: any;
      path: string[];
      message: string;
    }>;
  }>('/api/programs/admin', {
    method: 'POST',
    body: programData
  });
}

// V2 API - Create Program with ProgramTypes
export async function createProgramV2(requestBody: {
  program: any;
  programTypes: any[];
}) {
  try {
    console.log('🔍 DEBUG - createProgramV2: Making API call to:', `${API_URL}/api/programs-v2/add`);
    console.log('🔍 DEBUG - createProgramV2: Request body:', requestBody);
    console.log('🔍 DEBUG - createProgramV2: API_URL:', API_URL);
    
    const response = await apiRequest<{
      success: boolean;
      message: string;
      programId?: string;
      customProgramId?: string;
      programTypes?: string[];
      errors?: any[];
    }>('/api/programs-v2/add', {
      method: 'POST',
      body: requestBody
    });
    
    console.log('🔍 DEBUG - createProgramV2: API response:', response);
    return response;
  } catch (error) {
    console.error('Failed to create program V2:', error);
    return { 
      success: false, 
      message: 'Failed to create program',
      errors: []
    };
  }
}

export async function updateProgram(id: string, programData: any) {
  console.log('updateProgram called with:', { id, programData });
  
  try {
    const response = await apiRequest<{
      success?: boolean;
      statusCode?: number;
      message: string;
      data?: {
        program: {
          id: string;
          programId: string;
          title: string;
          status: string;
          payAsYouGo?: boolean;
          moduleCount?: number;
          baseModulesCount?: number;
          moduleConfigurationCount?: number;
          unifiedModulesCount?: number;
          createdAt: string;
        };
        modules: Array<{
          id: string;
          label: string;
          price: number;
          isActive: boolean;
          order: number;
        }>;
      };
      errors?: Array<{
        code: string;
        expected?: any;
        received?: any;
        path: string[];
        message: string;
      }>;
    }>(`/api/programs/admin/${id}`, {
      method: 'PUT',
      body: programData
    });
    
    console.log('updateProgram response:', response);
    return response;
  } catch (error) {
    console.error('updateProgram error:', error);
    throw error;
  }
}

export async function deleteProgram(id: string) {
  return apiRequest<{
    success?: boolean;
    statusCode?: number;
    message: string;
  }>(`/api/programs/admin/${id}`, {
    method: 'DELETE'
  });
}

// Updated category management functions
export async function fetchAllCategories() {
  return apiRequest<CategoriesResponse>('/api/addcategory', {
    method: 'GET'
  });
}

export async function fetchCategoryById(id: string) {
  return apiRequest<CategoryResponse>(`/api/addcategory/${id}`, {
    method: 'GET'
  });
}

export async function createCategory(category: {
  title: string;
  field: string;
  description: string;
  navOrder?: number;
  showInNav?: boolean;
}) {
  return apiRequest<CategoryResponse>('/api/addcategory', {
    method: 'POST',
    body: category
  });
}

export async function updateCategory(id: string, updates: {
  title?: string;
  field?: string;
  description?: string;
  navOrder?: number;
  showInNav?: boolean;
}) {
  return apiRequest<CategoryResponse>(`/api/addcategory/${id}`, {
    method: 'PUT',
    body: updates
  });
}

export async function deleteCategory(id: string) {
  return apiRequest<{ success: boolean; message: string }>(`/api/addcategory/${id}`, {
    method: 'DELETE',
    // Explicitly ensure no body is sent for DELETE request
    body: undefined
  });
}

export type StudentExportScope = "all" | "enrolled" | "not_enrolled" | "program";

export type StudentExportRow = {
  accountId: string;
  fullName: string;
  email: string;
  cellPhoneNumber: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  program: string;
  deliveryMode: string;
  dateOfBirth: string;
  graduation: string;
  lastLogin: string;
  createdAt: string;
  isBlocked: string;
};

export async function fetchStudentExportOptions(): Promise<string[]> {
  const res = await apiRequest<{
    success: boolean;
    data: { programs: string[] };
  }>("/api/admin/users/export/options");
  return res.data?.programs ?? [];
}

/** JSON export payload for client-side CSV/XLSX generation. */
export async function fetchStudentsForExport(params: {
  scope: StudentExportScope;
  program?: string;
}): Promise<{ students: StudentExportRow[]; count: number; truncated: boolean }> {
  const queryParams = new URLSearchParams();
  queryParams.set("scope", params.scope);
  queryParams.set("format", "json");
  if (params.scope === "program" && params.program) {
    queryParams.set("program", params.program);
  }
  const res = await apiRequest<{
    success: boolean;
    data: {
      students: StudentExportRow[];
      count: number;
      truncated: boolean;
    };
  }>(`/api/admin/users/export?${queryParams.toString()}`);
  return {
    students: res.data?.students ?? [],
    count: res.data?.count ?? 0,
    truncated: Boolean(res.data?.truncated),
  };
}

/** Direct CSV file download from the API. */
export async function downloadStudentsExportCsv(params: {
  scope: StudentExportScope;
  program?: string;
}): Promise<Blob> {
  const queryParams = new URLSearchParams();
  queryParams.set("scope", params.scope);
  queryParams.set("format", "csv");
  if (params.scope === "program" && params.program) {
    queryParams.set("program", params.program);
  }
  const response = await fetch(`${API_URL}/api/admin/users/export?${queryParams.toString()}`, {
    method: "GET",
    credentials: "include",
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { message?: string }).message ||
        `Failed to export students: ${response.statusText}`
    );
  }
  return response.blob();
}

// Updated student management functions
export async function fetchAllStudents(params?: {
  limit?: number;
  perPage?: number;
  page?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  active?: boolean;
  type?: 'students' | 'admins' | 'all';
  segment?: 'all' | 'students' | 'completed' | 'signups';
}) {
  const queryParams = new URLSearchParams();
  
  // perPage takes precedence over limit
  const itemsPerPage = params?.perPage || params?.limit || 20;
  queryParams.append('perPage', itemsPerPage.toString());
  if (params?.limit && !params?.perPage) queryParams.append('limit', params.limit.toString());
  
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.search) queryParams.append('search', params.search);
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params?.active !== undefined) queryParams.append('active', params.active.toString());
  if (params?.type) queryParams.append('type', params.type);
  if (params?.segment) queryParams.append('segment', params.segment);

  const queryString = queryParams.toString();
  // Use new /api/admin/users endpoint with pagination support
  const endpoint = `/api/admin/users${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<{
    success: boolean;
    message?: string;
    data: {
      users: Array<{
        _id: string;
        type: string;
        accountId: string;
        email: string;
        fullName?: string;
        cellPhoneNumber: string;
        isBlocked: boolean;
        isDeleted: boolean;
        createdAt: string;
        updatedAt: string;
        lastLogin?: string | null;
        lastCertificateDownload?: string | null;
        requiresPasswordReset?: boolean;
        program?: string | null;
        stage?: 'students' | 'completed' | 'signups';
        programEnrolled?: string | null;
        deliveryMode?: string | null;
        dateOfBirth?: string | null;
        graduation?: { month?: string | null; year?: string | null } | null;
        /** @deprecated List API no longer returns full certification docs */
        certification?: { programName?: string };
        firstName?: string;
        lastName?: string;
        orders?: Array<{
          _id: string;
          program: string;
          orderStatus: string;
          paymentStatus: string;
          createdAt: string;
        }>;
      }>;
      pagination: {
        total: number;
        page: number;
        perPage: number;
        limit: number;
        pages: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
      filters?: {
        type?: string;
        segment?: string;
        search?: string | null;
      };
      summary?: {
        segments?: {
          all: number;
          students: number;
          completed: number;
          signups: number;
        };
      };
    };
  }>(endpoint, {
    method: 'GET'
  });
}

export async function fetchStudentById(id: string, params?: {
  includeOrders?: boolean;
  includePayments?: boolean;
}) {
  const queryParams = new URLSearchParams();
  
  if (params?.includeOrders !== undefined) queryParams.append('includeOrders', params.includeOrders.toString());
  if (params?.includePayments !== undefined) queryParams.append('includePayments', params.includePayments.toString());

  const queryString = queryParams.toString();
  const endpoint = `/api/auth/students/${id}${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<{
    statusCode: number;
    data: {
      student: {
        _id: string;
        accountId: string;
        email: string;
        firstName: string;
        lastName: string;
        cellPhoneNumber: string;
        isBlocked: boolean;
        isDeleted: boolean;
        accountCreated: string;
        lastLogin: string;
      };
      courses: Array<{
        program: string;
        startDate: string;
        startMonth: string;
        deliveryMode: string;
        batch: string | null;
        status: string;
        agreeToTerms: boolean;
        termsAgreement?: {
          agreed: boolean;
          signature: {
            data: string;
            timestamp: string;
            ipAddress: string;
            userAgent: string;
          };
          termsVersion: string;
          consentDetails: {
            marketing: boolean;
            dataProcessing: boolean;
            thirdPartySharing: boolean;
          };
        };
        payment: {
          amount: number;
          transactionId: string;
          type: string;
          method: {
            type: string;
            lastFour: string;
            expiryMonth: string;
            expiryYear: string;
            cardType: string;
          };
          date: string;
          progress: {
            completedPayments: number;
            totalPayments: number;
            remainingPayments: number;
            status: string;
          };
        };
      }>;
      payments: Array<{
        _id: string;
        transactionId: string;
        amount: number;
        type: string;
        status: string;
        createdAt: string;
      }>;
      subscriptions: Array<{
        _id: string;
        squareSubscriptionId: string;
        plan: string;
        status: string;
        completedPayments: number;
        numberOfPayments: number;
        createdAt: string;
      }>;
    };
  }>(endpoint, {
    method: 'GET'
  });
}

export async function updateStudent(id: string, updates: {
  firstName?: string;
  lastName?: string;
  email?: string;
  cellPhoneNumber?: string;
  isBlocked?: boolean;
  notes?: string;
}) {
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      _id: string;
      accountId: string;
      email: string;
      firstName: string;
      lastName: string;
      cellPhoneNumber: string;
      isBlocked: boolean;
    };
  }>(`/api/auth/students/${id}`, {
    method: 'PUT',
    body: updates
  });
}

export async function deleteStudent(id: string) {
  return apiRequest<{
    statusCode: number;
    message: string;
  }>(`/api/auth/students/${id}`, {
    method: 'DELETE'
  });
}

// Updated subscription management functions
export async function createSubscriptionPlan(plan: {
  displayName: string;
  paymentOptionName?: string;
  amount: number;
  planType: string;
  numberOfPayments?: number;
  programId?: string;
  programDeliveryMode?: string;
}) {
  return apiRequest<{
    message: string;
    plan: {
      id?: string;
      type?: string;
      subscriptionPlanData?: any;
      displayName: string;
      amount: number;
      planType: string;
      numberOfPayments: number;
      isOneTime?: boolean;
      squarePlanId?: string | null;
      cadence?: string | null;
      slug?: string;
    };
  }>('/api/subscription/plan', {
    method: 'POST',
    body: plan
  });
}

export async function fetchSubscriptionPlans(params?: {
  includeInactive?: boolean;
}) {
  const queryParams = new URLSearchParams();
  
  if (params?.includeInactive) queryParams.append('includeInactive', 'true');

  const queryString = queryParams.toString();
  const endpoint = `/api/subscription/plan${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<{
    success: boolean;
    plans: Array<{
      displayName: string;
      squarePlanId: string;
      amount: number;
      cadence: string;
      numberOfPayments: number;
      status: string;
      planType: string;
      isOneTime: boolean;
      squareDetails: any;
      active: boolean;
    }>;
  }>(endpoint, {
    method: 'GET'
  });
}

export async function updateSubscriptionPlan(planId: string, updates: {
  displayName?: string;
  amount?: number;
  planType?: string;
  numberOfPayments?: number;
}) {
  return apiRequest<{
    success: boolean;
    message: string;
    plan: {
      id: string;
      slug: string;
      squarePlanId: string;
    };
  }>(`/api/subscription/plan/${planId}`, {
    method: 'PUT',
    body: updates
  });
}

export async function deleteSubscriptionPlan(planId: string) {
  return apiRequest<{
    success: boolean;
    message: string;
    plan: {
      id: string;
      slug: string;
      squarePlanId: string;
    };
  }>(`/api/subscription/plan/${planId}`, {
    method: 'DELETE'
  });
}

export async function fetchPaymentHistory(params: {
  studentId?: string;
  orderId?: string;
  transactionId?: string;
  status?: string;
  type?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  page?: number;
  search?: string;
  includeSquareData?: boolean;
  include?: string;
} = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.studentId) queryParams.append('studentId', params.studentId);
  if (params.orderId) queryParams.append('orderId', params.orderId);
  if (params.transactionId) queryParams.append('transactionId', params.transactionId);
  if (params.status) queryParams.append('status', params.status);
  if (params.type) queryParams.append('type', params.type);
  if (params.fromDate) queryParams.append('fromDate', params.fromDate);
  if (params.toDate) queryParams.append('toDate', params.toDate);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.search) queryParams.append('search', params.search);
  if (params.includeSquareData) queryParams.append('includeSquareData', 'true');
  if (params.include) queryParams.append('include', params.include);

  const queryString = queryParams.toString();
  return apiRequest<{
    success: boolean;
    data: {
      payments: Array<{
        _id: string;
        orderId: string;
        studentId: string;
        transactionId: string;
        amount: number;
        type: string;
        status: string;
        paymentMethod?: {
          lastFour: string;
          cardType: string;
        };
        timestamp: string;
        order?: {
          program: string;
          deliveryMode: string;
          paymentType: string;
        };
        student?: {
          fullName: string;
          email: string;
          accountId: string;
        };
        squareData?: any;
      }>;
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
      analytics?: any;
      summary?: any;
      upcoming?: any;
    };
  }>(`/api/admin/auth/payments${queryString ? `?${queryString}` : ''}`, {
    method: 'GET'
  });
}

export async function fetchStudentUpcomingPayments(studentId: string, params?: {
  includePast?: boolean;
  limit?: number;
  includeSquareData?: boolean;
}) {
  return fetchPaymentHistory({
    studentId,
    include: 'upcoming',
    limit: params?.limit,
    includeSquareData: params?.includeSquareData
  });
}

export async function fetchStudentPaymentHistory(studentId: string, params: {
  fromDate?: string;
  toDate?: string;
  status?: string;
  type?: string;
  limit?: number;
  page?: number;
  includeSquareData?: boolean;
} = {}) {
  return fetchPaymentHistory({
    studentId,
    fromDate: params.fromDate,
    toDate: params.toDate,
    status: params.status,
    type: params.type,
    limit: params.limit,
    page: params.page,
    includeSquareData: params.includeSquareData
  });
}

export async function fetchStudentPaymentSummary(studentId: string, _params: {
  timeRange?: number;
} = {}) {
  return fetchPaymentHistory({
    studentId,
    include: 'summary',
    limit: 1
  });
}

export async function fetchStudentsWithPaymentIssues(params: {
  issueType?: string;
  severity?: string;
  limit?: number;
  page?: number;
} = {}) {
  const queryParams = new URLSearchParams();
  
  if (params?.issueType) queryParams.append('issueType', params.issueType);
  if (params?.severity) queryParams.append('severity', params.severity);
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.page) queryParams.append('page', params.page.toString());

  const queryString = queryParams.toString();
  const endpoint = `/api/admin/auth/payments/students/issues${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      students: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        accountId: string;
        issues: Array<{
          type: string;
          severity: string;
          description: string;
          lastOccurrence: string;
          count: number;
        }>;
        paymentSummary: {
          totalOutstanding: number;
          failedPayments: number;
          activeSubscriptions: number;
        };
      }>;
      summary: {
        totalStudents: number;
        highSeverity: number;
        mediumSeverity: number;
        lowSeverity: number;
        byIssueType: Record<string, number>;
      };
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
    };
  }>(endpoint, {
    method: 'GET'
  });
}

// Square API Integration Routes
export async function searchSquarePayments(params: {
  beginTime?: string;
  endTime?: string;
  sortOrder?: 'ASC' | 'DESC';
  limit?: number;
  locationId?: string;
  total?: string;
  last4?: string;
  cardBrand?: string;
} = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.beginTime) queryParams.append('beginTime', params.beginTime);
  if (params.endTime) queryParams.append('endTime', params.endTime);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.locationId) queryParams.append('locationId', params.locationId);
  if (params.total) queryParams.append('total', params.total);
  if (params.last4) queryParams.append('last4', params.last4);
  if (params.cardBrand) queryParams.append('cardBrand', params.cardBrand);

  const queryString = queryParams.toString();
  return apiRequest<{
    // Support flat structure
    success?: boolean;
    payments?: Array<{
      id: string;
      created_at: string;
      amount_money: {
        amount: string;
        currency: string;
      };
      status: string;
      card_details?: {
        card?: {
          card_brand: string;
          last_4: string;
        };
      };
    }>;
    cursor?: string;
    // Also support nested structure for backwards compatibility
    statusCode?: number;
    message?: string;
    data?: {
      success: boolean;
      payments: Array<{
        id: string;
        created_at: string;
        amount_money: {
          amount: string;
          currency: string;
        };
        status: string;
        card_details?: {
          card?: {
            card_brand: string;
            last_4: string;
          };
        };
      }>;
      cursor?: string;
    };
  }>(`/api/admin/auth/square/payments${queryString ? `?${queryString}` : ''}`);
}

export async function searchSquareSubscriptions(params: {
  locationIds?: string;
  cursor?: string;
  limit?: number;
} = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.locationIds) queryParams.append('locationIds', params.locationIds);
  if (params.cursor) queryParams.append('cursor', params.cursor);
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const queryString = queryParams.toString();
  return apiRequest<{
    statusCode?: number;
    message?: string;
    subscriptions?: Array<{
        id: string;
        status: string;
      planVariationId?: string;
      plan_variation_id?: string;
      customerId?: string;
      customer_id?: string;
      startDate?: string;
      start_date?: string;
      chargedThroughDate?: string;
      charged_through_date?: string;
      locationId?: string;
      location_id?: string;
      invoiceIds?: string[];
      invoice_ids?: string[];
      createdAt?: string;
      created_at?: string;
      cardId?: string;
      card_id?: string;
      monthlyBillingAnchorDate?: number;
      monthly_billing_anchor_date?: number;
      completedDate?: string;
      completed_date?: string;
      billing_end_date?: string;
    }>;
    data?: {
      success?: boolean;
      subscriptions?: Array<{
        id: string;
        status: string;
        planVariationId?: string;
        plan_variation_id?: string;
        customerId?: string;
        customer_id?: string;
        startDate?: string;
        start_date?: string;
        chargedThroughDate?: string;
        charged_through_date?: string;
        locationId?: string;
        location_id?: string;
        invoiceIds?: string[];
        invoice_ids?: string[];
        createdAt?: string;
        created_at?: string;
        cardId?: string;
        card_id?: string;
        monthlyBillingAnchorDate?: number;
        monthly_billing_anchor_date?: number;
        completedDate?: string;
        completed_date?: string;
        billing_end_date?: string;
      }>;
      cursor?: string;
    };
    cursor?: string;
  }>(`/api/admin/auth/square/subscriptions${queryString ? `?${queryString}` : ''}`);
}

// Payment Analytics
export async function getPaymentAnalytics(params: {
  fromDate?: string;
  toDate?: string;
  granularity?: 'day' | 'week' | 'month';
} = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.fromDate) queryParams.append('fromDate', params.fromDate);
  if (params.toDate) queryParams.append('toDate', params.toDate);
  if (params.granularity) queryParams.append('granularity', params.granularity);

  const queryString = queryParams.toString();
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      success: boolean;
      period: {
        fromDate: string;
        toDate: string;
        granularity: string;
      };
      summary: {
        totalPayments: number;
        totalRefunds: number;
        totalRevenue: number;
        successfulPayments: number;
        failedPayments: number;
        successRate: string;
      };
      timeline: Array<{
        _id: {
          year: number;
          month: number;
          day: number;
        };
        data: Array<{
          status: string;
          type: string;
          count: number;
          totalAmount: number;
        }>;
        totalTransactions: number;
        totalRevenue: number;
      }>;
    };
  }>(`/api/admin/auth/payments/analytics${queryString ? `?${queryString}` : ''}`);
}

// Payment Consistency API functions
export async function getUpcomingPaymentsSummary(params: {
  includeErrors?: boolean;
} = {}) {
  const queryParams = new URLSearchParams();
  if (params.includeErrors) queryParams.append('includeErrors', 'true');
  
  const queryString = queryParams.toString();
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      totalUpcoming: number;
      totalAmount: number;
      next7Days: {
        count: number;
        amount: number;
      };
      next30Days: {
        count: number;
        amount: number;
      };
      next90Days: {
        count: number;
        amount: number;
      };
      thisMonth: {
        count: number;
        amount: number;
      };
      nextMonth: {
        count: number;
        amount: number;
      };
      errors?: Array<{
        type: string;
        message: string;
        subscriptionId?: string;
      }>;
      students?: Array<{
        studentId: string;
        fullName: string;
        email: string;
        accountId: string;
        paymentSummary: {
          totalAmountPaid: number;
          totalAmountRemaining: number;
          totalAmount: number;
          totalCompletedPayments: number;
          totalRemainingPayments: number;
        };
        nextPayment: {
          date: string;
          amount: number;
        };
        subscriptions: Array<{
          subscriptionId: string;
          squareSubscriptionId: string;
          programName: string;
          amountPaid: number;
          amountRemaining: number;
          totalAmount: number;
          completedPayments: number;
          remainingPayments: number;
          numberOfPayments: number;
          nextPaymentDate: string;
        }>;
      }>;
    };
  }>(`/api/admin/payment-consistency/upcoming-summary${queryString ? `?${queryString}` : ''}`, {
    method: 'GET'
  });
}

export async function diagnoseSubscription(subscriptionId: string) {
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      subscriptionId: string;
      status: string;
      issues: Array<{
        type: string;
        severity: string;
        description: string;
      }>;
      recommendations: Array<string>;
    };
  }>(`/api/admin/payment-consistency/diagnose/${subscriptionId}`, {
    method: 'GET'
  });
}

export async function getPaymentConsistencyIssues(params: {
  limit?: number;
  page?: number;
  severity?: 'high' | 'medium' | 'low';
} = {}) {
  const queryParams = new URLSearchParams();
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.severity) queryParams.append('severity', params.severity);
  
  const queryString = queryParams.toString();
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      success: boolean;
      totalRecords: number;
      orders: Array<{
        _id: string;
        program: string;
        paymentStatus: string;
        paymentTransactionId: string;
        paymentAmount: number;
        orderStatus: string;
        issueType: string;
        severity: 'high' | 'medium' | 'low';
        student: {
          _id: string;
          firstName: string;
          lastName: string;
          email: string;
        };
      }>;
      summary: {
        highSeverity: number;
        mediumSeverity: number;
        lowSeverity: number;
      };
    };
  }>(`/api/admin/payment-consistency/payment-issues${queryString ? `?${queryString}` : ''}`, {
    method: 'GET'
  });
}

export async function getStudentsWithConsistencyIssues(params: {
  issueType?: 'failed_payments' | 'overdue_subscriptions' | 'payment_method_problems' | 'all';
  severity?: 'high' | 'medium' | 'low';
  limit?: number;
  page?: number;
} = {}) {
  const queryParams = new URLSearchParams();
  if (params.issueType) queryParams.append('issueType', params.issueType);
  if (params.severity) queryParams.append('severity', params.severity);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.page) queryParams.append('page', params.page.toString());
  
  const queryString = queryParams.toString();
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      students: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        accountId: string;
        issues: Array<{
          type: string;
          severity: string;
          description: string;
          lastOccurrence: string;
          count: number;
        }>;
        paymentSummary: {
          totalOutstanding: number;
          failedPayments: number;
          activeSubscriptions: number;
        };
      }>;
      summary: {
        totalStudents: number;
        highSeverity: number;
        mediumSeverity: number;
        lowSeverity: number;
        byIssueType: Record<string, number>;
      };
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
    };
  }>(`/api/admin/payment-consistency/students-with-issues${queryString ? `?${queryString}` : ''}`, {
    method: 'GET'
  });
}

// Checkout Tracking API functions
export async function getCheckoutTrackingEvents(params: {
  studentId?: string;
  orderId?: string;
  subscriptionId?: string;
  eventType?: string;
  status?: string;
  timing?: 'on-time' | 'delayed' | 'early' | 'overdue';
  startDate?: string;
  endDate?: string;
  fromDate?: string;
  toDate?: string;
  limit?: number;
  page?: number;
  include?: string;
} = {}) {
  const queryParams = new URLSearchParams();
  if (params.studentId) queryParams.append('studentId', params.studentId);
  if (params.orderId) queryParams.append('orderId', params.orderId);
  if (params.subscriptionId) queryParams.append('subscriptionId', params.subscriptionId);
  if (params.eventType) queryParams.append('eventType', params.eventType);
  if (params.status) queryParams.append('status', params.status);
  if (params.timing) queryParams.append('timing', params.timing);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.fromDate) queryParams.append('startDate', params.fromDate);
  if (params.toDate) queryParams.append('endDate', params.toDate);
  if (params.limit) queryParams.append('limit', params.limit.toString());
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.include) queryParams.append('include', params.include);

  const queryString = queryParams.toString();
  return apiRequest<{
    success: boolean;
    data: {
      events: Array<{
        _id: string;
        timestamp: string;
        eventType: string;
        status: string;
        timing: 'ON_TIME' | 'DELAYED' | 'EARLY' | 'OVERDUE';
        student?: {
          _id: string;
          fullName: string;
          email: string;
        };
        order?: {
          _id: string;
          program: string;
        };
        subscription?: {
          _id: string;
          squareSubscriptionId: string;
        };
        amount?: number;
        scheduledDate?: string;
        actualDate?: string;
        delayMinutes?: number;
        metadata?: any;
      }>;
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
      summary?: {
        totalEvents: number;
        successfulPayments: number;
        failedPayments: number;
        pendingPayments: number;
        averageDelay: number;
        onTimeRate: number;
        delayedPayments: number;
        overduePayments: number;
        cardChanges: number;
        cardDeclines: number;
        totalRevenue: number;
        totalRefunds: number;
        totalChargebacks: number;
        totalDelayFees: number;
      };
    };
  }>(`/api/admin/auth/checkout-tracking${queryString ? `?${queryString}` : ''}`, {
    method: 'GET'
  });
}

export async function getCheckoutTrackingSummary(params: {
  startDate?: string;
  endDate?: string;
  fromDate?: string;
  toDate?: string;
} = {}) {
  const response = await getCheckoutTrackingEvents({
    startDate: params.startDate || params.fromDate,
    endDate: params.endDate || params.toDate,
    limit: 1,
    include: 'summary'
  });
  return {
    ...response,
    data: {
      ...response.data,
      successRate: response.data.summary?.onTimeRate || 0,
      averageDelay: response.data.summary?.averageDelay || 0,
      totalRevenue: response.data.summary?.totalRevenue || 0,
      totalEvents: response.data.summary?.totalEvents || 0,
      eventsByType: {},
      eventsByStatus: {},
      eventsByTiming: {}
    }
  };
}

export async function getStudentCheckoutTracking(studentId: string, params: {
  startDate?: string;
  endDate?: string;
  fromDate?: string;
  toDate?: string;
  eventType?: string;
  status?: string;
  limit?: number;
  page?: number;
} = {}) {
  return getCheckoutTrackingEvents({
    studentId,
    startDate: params.startDate || params.fromDate,
    endDate: params.endDate || params.toDate,
    eventType: params.eventType,
    status: params.status,
    limit: params.limit,
    page: params.page,
    include: 'summary'
  });
}

export async function getCheckoutEventTypes() {
  return apiRequest<{
    statusCode: number;
    message: string;
    data: string[];
  }>('/api/admin/auth/checkout-tracking/event-types', {
    method: 'GET'
  });
}

// New Checkout Tracking API Functions
export const checkoutTrackingApi = {
  // Failed Checkouts Monitor
  getFailedCheckouts: async (params?: {
    hours?: number;
    limit?: number;
    page?: number;
    errorType?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.hours) queryParams.append('hours', params.hours.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.errorType) queryParams.append('errorType', params.errorType);

    const queryString = queryParams.toString();
    return apiRequest<{
      success: boolean;
      data: {
        failedCheckouts: Array<{
          _id: string;
          studentId: {
            _id: string;
            fullName: string;
            email: string;
            accountId: string;
            cellPhoneNumber?: string;
          };
          orderId: string;
          subscriptionId: string;
          transactionId: string;
          amount: number;
          programName: string;
          programId: string;
          deliveryMode: string;
          paymentType: string;
          planId: string;
          reason: string;
          metadata?: any;
          createdAt: string;
          updatedAt: string;
        }>;
        errorBreakdown: Record<string, number>;
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
        timeRange: {
          hours: number;
          from: string;
          to: string;
        };
      };
    }>(`/api/admin/checkout-tracking/failed${queryString ? `?${queryString}` : ''}`, {
      method: 'GET'
    });
  },

  // Funnel Analytics
  getFunnelAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
    programId?: string;
    deliveryMode?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.programId) queryParams.append('programId', params.programId);
    if (params?.deliveryMode) queryParams.append('deliveryMode', params.deliveryMode);

    const queryString = queryParams.toString();
    return apiRequest<{
      success: boolean;
      data: {
        funnel: {
          started: number;
          cardAdded: number;
          completed: number;
          failed: number;
        };
        conversionRates: {
          startedToCard: number;
          cardToCompleted: number;
          overallConversion: number;
        };
        averageTimes: {
          timeToCardSeconds: number;
          timeToCardMinutes: number;
          timeToCompleteSeconds: number;
          timeToCompleteMinutes: number;
        };
        failureReasons: Record<string, number>;
        filters: {
          startDate?: string;
          endDate?: string;
          programId?: string;
          deliveryMode?: string;
        };
      };
    }>(`/api/admin/checkout-tracking/analytics/funnel${queryString ? `?${queryString}` : ''}`, {
      method: 'GET'
    });
  },

  // Timeline Debugger
  getCheckoutTimeline: async (transactionId: string) => {
    return apiRequest<{
      success: boolean;
      data: {
        transactionId: string;
        totalEvents: number;
        timeline: Array<{
          _id: string;
          eventType: string;
          status: string;
          studentId?: {
            _id: string;
            fullName: string;
            email: string;
            accountId: string;
          };
          amount?: number;
          orderId?: any;
          subscriptionId?: any;
          metadata?: any;
          createdAt: string;
        }>;
        eventsByType: Record<string, any[]>;
        linkedEvents: Array<{
          started?: any;
          completed?: any;
          cardAdded?: any[];
          paymentSuccess?: any[];
        }>;
        summary: {
          student: {
            _id: string;
            fullName: string;
            email: string;
            accountId: string;
          };
          order?: any;
          subscription?: any;
          status: string;
          amount: number;
          programName: string;
        };
      };
    }>(`/api/admin/checkout-tracking/timeline/${encodeURIComponent(transactionId)}`, {
      method: 'GET'
    });
  },
};

// Test function to check API connectivity
export async function testApiConnection() {
  try {
    const response = await fetch(`${API_URL}/api/programs/admin/PRG00128745`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      return { success: true, data };
    } else {
      const errorText = await response.text();
      return { success: false, status: response.status, error: errorText };
    }
  } catch (error) {
    console.error('Test API connection error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Debug function to check admin authentication status
export async function debugAdminAuth() {
  console.log('🔍 Debugging Admin Authentication...');
  
  try {
    // 1. Check if we have any cookies
    console.log('📋 Current cookies:', document.cookie);
    
    // 2. Test the /me endpoint
    console.log('🧪 Testing /api/admin/auth/me endpoint...');
    const meResponse = await fetch(`${API_URL}${ADMIN_ENDPOINTS.me}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    console.log('Me endpoint response:', {
      status: meResponse.status,
      statusText: meResponse.statusText,
      ok: meResponse.ok,
    });
    
    if (meResponse.ok) {
      const meData = await meResponse.json();
      console.log('Me endpoint data:', meData);
      return { success: true, data: meData };
    } else {
      const errorText = await meResponse.text();
      console.log('Me endpoint error:', errorText);
      
      // 3. Try to refresh the session
      console.log('🔄 Attempting to refresh admin session...');
      const refreshResult = await refreshAdminSession();
      console.log('Refresh result:', refreshResult);
      
      return { success: false, error: errorText, refreshResult };
    }
  } catch (error) {
    console.error('Debug auth error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Enhanced debug function to check authentication status and cookies
export function debugAuthStatus() {
  const debugInfo = {
    timestamp: new Date().toISOString(),
    currentDomain: typeof window !== 'undefined' ? window.location.origin : 'N/A',
    apiUrl: API_URL,
    cookies: {
      all: typeof document !== 'undefined' ? document.cookie : 'N/A',
      hasAccessToken: typeof document !== 'undefined' ? document.cookie.includes('accessToken') : false,
      hasRefreshToken: typeof document !== 'undefined' ? document.cookie.includes('adminRefreshToken') : false,
      cookieCount: typeof document !== 'undefined' ? document.cookie.split(';').length : 0,
      // Detailed cookie breakdown
      cookieDetails: typeof document !== 'undefined' ? 
        document.cookie.split(';').reduce((acc, cookie) => {
          const [name, value] = cookie.trim().split('=');
          if (name && (name.toLowerCase().includes('admin') || name.toLowerCase().includes('token') || name.toLowerCase().includes('auth'))) {
            acc[name] = value ? `${value.substring(0, 10)}...` : 'empty';
          }
          return acc;
        }, {} as Record<string, string>) : {}
    },
    localStorage: {
      note: 'No auth data in localStorage - using HTTP-only cookies',
      lastActivity: typeof window !== 'undefined' ? localStorage.getItem('lastActivity') : 'N/A'
    }
  };
  
  console.log('🔍 Authentication Debug Info:', debugInfo);
  
  // Additional cookie analysis
  if (typeof document !== 'undefined' && document.cookie) {
    console.log('🍪 Cookie Analysis:');
    document.cookie.split(';').forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      console.log(`  ${name}: ${value ? value.substring(0, 20) + '...' : 'no value'}`);
    });
  }
  
  return debugInfo;
}

// Function to check if we have valid cookies
export function hasValidCookies(): boolean {
  if (typeof document === 'undefined') return false;
  
  const hasAccess = document.cookie.includes('adminAccessToken');
  const hasRefresh = document.cookie.includes('adminRefreshToken');
  
  console.log('🍪 Cookie Check:', { hasAccess, hasRefresh });
  return hasAccess || hasRefresh;
}

// Function to test session validation
export async function testSessionValidation() {
  try {
    console.log('🧪 Testing session validation...');
    
    // Test the /api/admin/auth/me endpoint
    const response = await fetch(`${API_URL}${ADMIN_ENDPOINTS.me}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      mode: 'cors',
    });
    
    console.log('Session validation response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log('Session validation successful:', data);
      return { success: true, data };
    } else {
      const errorText = await response.text();
      console.log('Session validation failed:', errorText);
      return { success: false, status: response.status, error: errorText };
    }
  } catch (error) {
    console.error('Session validation test error:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Analytics API
export const analyticsApi = {
  // Get revenue analytics
  getRevenueAnalytics: (params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
    includeRefunds?: boolean;
    includeSubscriptions?: boolean;
    includeOneTime?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.groupBy) queryParams.append('groupBy', params.groupBy);
    if (params?.includeRefunds !== undefined) queryParams.append('includeRefunds', params.includeRefunds.toString());
    if (params?.includeSubscriptions !== undefined) queryParams.append('includeSubscriptions', params.includeSubscriptions.toString());
    if (params?.includeOneTime !== undefined) queryParams.append('includeOneTime', params.includeOneTime.toString());

    const queryString = queryParams.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        summary: {
          totalRevenue: number;
          totalPayments: number;
          totalRefunds: number;
          netRevenue: number;
          averageTransactionValue: number;
          successRate: number;
        };
        trends: {
          daily: Array<{
            date: string;
            revenue: number;
            payments: number;
            refunds: number;
            netRevenue: number;
          }>;
          weekly: Array<{
            week: string;
            revenue: number;
            payments: number;
            refunds: number;
            netRevenue: number;
          }>;
          monthly: Array<{
            month: string;
            revenue: number;
            payments: number;
            refunds: number;
            netRevenue: number;
          }>;
        };
        breakdown: {
          byPaymentType: {
            subscription: {
              count: number;
              amount: number;
              percentage: number;
            };
            oneTime: {
              count: number;
              amount: number;
              percentage: number;
            };
          };
          byStatus: {
            completed: {
              count: number;
              amount: number;
              percentage: number;
            };
            failed: {
              count: number;
              amount: number;
              percentage: number;
            };
          };
          detailedPaymentStatus: {
            completed: {
              count: number;
              amount: number;
            };
            failed: {
              count: number;
              amount: number;
            };
            declined: {
              count: number;
              amount: number;
            };
            pending: {
              count: number;
              amount: number;
            };
            processing: {
              count: number;
              amount: number;
            };
            cancelled: {
              count: number;
              amount: number;
            };
          };
        };
        recentPayments: Array<{
          paymentId: string;
          userId: string;
          userEmail: string;
          userName: string;
          amount: number;
          status: string;
          paymentMethod: string;
          createdAt: string;
          description: string;
          refundedAmount: number;
          cardBrand?: string;
          last4?: string;
        }>;
        topCustomers: Array<{
          userId: string;
          userEmail: string;
          userName: string;
          totalSpent: number;
          paymentCount: number;
          lastPaymentDate: string;
        }>;
        paymentInsights: {
          averagePaymentValue: number;
          highestPayment: number;
          lowestPayment: number;
          paymentMethods: Record<string, number>;
          cardBrands: Record<string, number>;
        };
        cached: boolean;
        lastUpdated: string;
      };
    }>(`/api/analytics/revenue${queryString ? `?${queryString}` : ''}`);
  },

  // Get recent activity
  getRecentActivity: (params?: {
    hours?: number;
    includeAlerts?: boolean;
    includeMetrics?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params?.hours) queryParams.append('hours', params.hours.toString());
    if (params?.includeAlerts !== undefined) queryParams.append('includeAlerts', params.includeAlerts.toString());
    if (params?.includeMetrics !== undefined) queryParams.append('includeMetrics', params.includeMetrics.toString());

    const queryString = queryParams.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        activity: {
          totalTransactions: number;
          totalAmount: number;
          averageTransactionValue: number;
          paymentMethods: Record<string, number>;
          topPaymentMethods: Array<{
            method: string;
            count: number;
            percentage: number;
          }>;
          paymentStatusBreakdown: {
            completed: {
              count: number;
              amount: number;
              percentage: number;
            };
            failed: {
              count: number;
              amount: number;
              percentage: number;
            };
            declined: {
              count: number;
              amount: number;
              percentage: number;
            };
            pending: {
              count: number;
              amount: number;
              percentage: number;
            };
            processing: {
              count: number;
              amount: number;
              percentage: number;
            };
            cancelled: {
              count: number;
              amount: number;
              percentage: number;
            };
          };
        };
        metrics: {
          hourlyBreakdown: Array<{
            hour: number;
            transactions: number;
            amount: number;
          }>;
          successRate: number;
          peakHour: number;
          peakAmount: number;
        };
        alerts: Array<{
          type: string;
          severity: string;
          message: string;
          details: any;
        }>;
        timeframe: {
          start: string;
          end: string;
          duration: string;
        };
      };
    }>(`/api/analytics/recent-activity${queryString ? `?${queryString}` : ''}`);
  },

  // Get revenue comparison
  getRevenueComparison: (params: {
    period1Start: string;
    period1End: string;
    period2Start: string;
    period2End: string;
    metrics?: string;
  }) => {
    const queryParams = new URLSearchParams();
    
    queryParams.append('period1Start', params.period1Start);
    queryParams.append('period1End', params.period1End);
    queryParams.append('period2Start', params.period2Start);
    queryParams.append('period2End', params.period2End);
    if (params.metrics) queryParams.append('metrics', params.metrics);

    const queryString = queryParams.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        period1: {
          start: string;
          end: string;
          revenue: number;
          payments: number;
          refunds: number;
          netRevenue: number;
        };
        period2: {
          start: string;
          end: string;
          revenue: number;
          payments: number;
          refunds: number;
          netRevenue: number;
        };
        comparison: {
          revenue: {
            change: number;
            percentage: number;
            trend: string;
          };
          payments: {
            change: number;
            percentage: number;
            trend: string;
          };
          refunds: {
            change: number;
            percentage: number;
            trend: string;
          };
        };
        insights: string[];
      };
    }>(`/api/analytics/revenue/comparison?${queryString}`);
  },

  // Get monitoring statistics
  getMonitoringStats: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        performance: {
          averageResponseTime: number;
          totalRequests: number;
          successRate: number;
          errorRate: number;
          peakResponseTime: number;
        };
        cache: {
          hitRate: number;
          totalRequests: number;
          cacheHits: number;
          cacheMisses: number;
          averageCacheTime: number;
        };
        squareApi: {
          totalCalls: number;
          successRate: number;
          averageResponseTime: number;
          errorRate: number;
          lastCall: string;
        };
        alerts: Array<{
          type: string;
          severity: string;
          message: string;
          timestamp: string;
          value: number;
          threshold: number;
        }>;
        systemHealth: {
          status: string;
          lastCheck: string;
          uptime: number;
          memoryUsage: number;
          cpuUsage: number;
        };
      };
    }>('/api/analytics/monitoring/stats'),

  // Get system health
  getSystemHealth: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        status: string;
        timestamp: string;
        checks: {
          database: {
            status: string;
            responseTime: number;
            connections: number;
          };
          squareApi: {
            status: string;
            responseTime: number;
            lastCall: string;
          };
          cache: {
            status: string;
            hitRate: number;
            size: number;
          };
          fileSystem: {
            status: string;
            availableSpace: number;
            usedSpace: number;
          };
        };
        metrics: {
          uptime: number;
          memoryUsage: number;
          cpuUsage: number;
          activeConnections: number;
        };
        alerts: any[];
      };
    }>('/api/analytics/monitoring/health'),

  // Clear analytics cache
  clearAnalyticsCache: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        clearedEntries: number;
        cacheSize: number;
        clearedAt: string;
      };
    }>('/api/analytics/monitoring/cache/clear', {
      method: 'POST'
    }),

  // Export analytics data
  exportUserReport: (userId: string, params?: {
    includePaymentHistory?: boolean;
    includeEnrollmentDetails?: boolean;
    includeAnalytics?: boolean;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params?.includePaymentHistory !== undefined) queryParams.append('includePaymentHistory', params.includePaymentHistory.toString());
    if (params?.includeEnrollmentDetails !== undefined) queryParams.append('includeEnrollmentDetails', params.includeEnrollmentDetails.toString());
    if (params?.includeAnalytics !== undefined) queryParams.append('includeAnalytics', params.includeAnalytics.toString());

    const queryString = queryParams.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        filename: string;
        size: number;
        pages: number;
        generatedAt: string;
        downloadUrl: string;
      };
    }>(`/api/analytics/export/pdf/${userId}${queryString ? `?${queryString}` : ''}`);
  },

  // Export enrollment data
  exportEnrollmentData: (params?: {
    filters?: string;
    includePaymentData?: boolean;
    includeAnalytics?: boolean;
    dateRange?: string;
  }) => {
    const queryParams = new URLSearchParams();
    
    if (params?.filters) queryParams.append('filters', params.filters);
    if (params?.includePaymentData !== undefined) queryParams.append('includePaymentData', params.includePaymentData.toString());
    if (params?.includeAnalytics !== undefined) queryParams.append('includeAnalytics', params.includeAnalytics.toString());
    if (params?.dateRange) queryParams.append('dateRange', params.dateRange);

    const queryString = queryParams.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        filename: string;
        size: number;
        rows: number;
        columns: number;
        generatedAt: string;
        downloadUrl: string;
      };
    }>(`/api/analytics/export/excel${queryString ? `?${queryString}` : ''}`);
  },

  // Get export status
  getExportStatus: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        recentExports: Array<{
          id: string;
          type: string;
          filename: string;
          status: string;
          createdAt: string;
          completedAt?: string;
          size?: number;
          downloadUrl?: string;
          progress?: number;
        }>;
        availableDownloads: Array<{
          filename: string;
          type: string;
          size: number;
          createdAt: string;
          downloadUrl: string;
        }>;
        storage: {
          totalFiles: number;
          totalSize: number;
          maxSize: number;
          usagePercentage: number;
        };
      };
    }>('/api/analytics/export/status')
}; 

// Add a function to validate admin session
export async function validateAdminSession(): Promise<boolean> {
  try {
    const response = await authApi.sessionStatus();
    return response.isActive === true && !!response.user;
  } catch (error) {
    console.error('Session validation error:', error);
    return false;
  }
}

// Function to refresh admin session
export async function refreshAdminSession(): Promise<boolean> {
  try {
    // Use the configured admin refresh endpoint (now points to /api/auth/refresh)
    const response = await fetch(`${API_URL}${ADMIN_ENDPOINTS.refresh}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    
    if (response.ok) {
      console.log('Admin session refreshed successfully');
      return true;
    }
    
    console.warn('Admin session refresh failed:', response.status);
    return false;
  } catch (error) {
    console.error('Admin session refresh error:', error);
    return false;
  }
}

// Admin Signature API - Updated to handle signature IDs
export async function fetchAdminSignature(signatureId: string) {
  const endpoint = `/api/admin/auth/signatures/${signatureId}`;
  
  return apiRequest<{
    statusCode: number;
    message: string;
    data: {
      signatureId: string;
      type: string;
      signature: {
        s3Key: string;
        s3Url: string;
        timestamp: string;
        ipAddress: string;
        userAgent: string;
      };
      termsVersion: string;
      consentDetails: {
        marketing: boolean;
        dataProcessing: boolean;
        thirdPartySharing: boolean;
      };
      student: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        accountId: string;
      };
      program: string;
      createdAt: string;
    };
  }>(endpoint, {
    method: 'GET'
  });
}

// iSpring API calls
export const iSpringApi = {
  getCourseDetailsByTitle: async (title: string, retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(`${API_URL}/api/ispring/course/details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title })
        });

        if (!response.ok) {
          // Don't retry on 4xx errors (client errors)
          if (response.status >= 400 && response.status < 500) {
            return {
              success: false,
              data: null,
              error: `Client error: ${response.status}`
            };
          }
          // Retry on 5xx errors (server errors)
          if (attempt < retries) {
            console.warn(`Server error ${response.status}, retrying... (attempt ${attempt + 1}/${retries + 1})`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Exponential backoff
            continue;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error(`Error fetching course details (attempt ${attempt + 1}/${retries + 1}):`, error);
        
        // Don't retry on network errors or if this is the last attempt
        if (attempt === retries) {
          return {
            success: false,
            data: null,
            error: error instanceof Error ? error.message : 'Failed to fetch course details'
          };
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
    
    return {
      success: false,
      data: null,
      error: 'Max retries exceeded'
    };
  }
};

// Ticket API
export const ticketApi = {
  // Get all tickets
  getTickets: (filters?: Record<string, any>) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        tickets: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(`/api/admin/tickets${qs ? `?${qs}` : ''}`);
  },

  // Get all tickets (alias for getTickets)
  getAllTickets: (filters?: Record<string, any>) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        tickets: any[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      };
    }>(`/api/admin/tickets${qs ? `?${qs}` : ''}`);
  },

  // Get single ticket (detail + messages + student context)
  getTicket: (ticketId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        ticket: any;
        messages: any[];
        messagesPagination?: {
          hasMoreOlder: boolean;
          totalCount: number;
          limit: number;
        };
        studentContext?: any;
        ticketHistory?: { totalTickets: number; openTickets: number };
      };
    }>(`/api/admin/tickets/${encodeURIComponent(ticketId)}`),

  getTicketMessages: (
    ticketId: string,
    params?: { beforeMessageId?: string; limit?: number }
  ) => {
    const query = new URLSearchParams();
    if (params?.beforeMessageId) query.set('beforeMessageId', params.beforeMessageId);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        messages: any[];
        pagination: {
          hasMoreOlder: boolean;
          totalCount: number;
          limit: number;
        };
      };
    }>(`/api/admin/tickets/${encodeURIComponent(ticketId)}/messages${qs ? `?${qs}` : ''}`);
  },

  getPresence: (ticketId: string) =>
    apiRequest<{
      statusCode?: number;
      success?: boolean;
      message?: string;
      data: {
        ticketId?: string;
        participants: Array<{
          userId: string;
          role: string;
          isPresent: boolean;
          lastSeenAt?: string;
          socketCount: number;
        }>;
      };
    }>(`/api/admin/tickets/${encodeURIComponent(ticketId)}/presence`),

  // Send message
  sendMessage: (
    ticketId: string,
    message: string,
    attachments?: Array<{ filename: string; storageKey: string; size: number; type: string }>
  ) =>
    apiRequest<{
      success: boolean;
      data: {
        message: any;
      };
    }>(`/api/admin/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: { message, attachments },
    }),

  // Update status
  updateStatus: (ticketId: string, status: string, estimatedResolutionTime?: string, reason?: string) => {
    const requestBody = { 
      status, 
      estimatedResolutionTime, 
      reason 
    };
    console.log('Updating ticket status:', { ticketId, requestBody });
    
    return apiRequest<{
      success: boolean;
      data: {
        ticket: any;
      };
    }>(`/api/admin/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: requestBody,
    });
  },

  // Update priority
  updatePriority: (ticketId: string, priority: string) =>
    apiRequest<{
      success: boolean;
      data: {
        ticket: any;
      };
    }>(`/api/admin/tickets/${ticketId}/priority`, {
      method: 'PATCH',
      body: { priority },
    }),

  // Update category
  updateCategory: (ticketId: string, category: string) =>
    apiRequest<{
      success: boolean;
      data: {
        ticket: any;
      };
    }>(`/api/admin/tickets/${ticketId}/category`, {
      method: 'PATCH',
      body: { category },
    }),

  // Assign ticket
  assignTicket: (ticketId: string, adminId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { ticket: any };
    }>(`/api/admin/tickets/${encodeURIComponent(ticketId)}/assign`, {
      method: 'PUT',
      body: { adminId },
    }),

  markInProcess: (ticketId: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/in-process`, {
      method: 'PATCH',
    }),

  closeTicket: (ticketId: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/close`, {
      method: 'PATCH',
    }),

  restoreTicket: (ticketId: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/restore`, {
      method: 'POST',
    }),

  editMessage: (ticketId: string, messageId: string, message: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}`, {
      method: 'PATCH',
      body: { message },
    }),

  deleteMessage: (ticketId: string, messageId: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}`, {
      method: 'DELETE',
    }),

  unsendMessage: (ticketId: string, messageId: string) =>
    apiRequest(`/api/admin/tickets/${encodeURIComponent(ticketId)}/messages/${encodeURIComponent(messageId)}/unsend`, {
      method: 'POST',
    }),

  presignAttachment: (
    ticketId: string,
    input: { fileName: string; contentType: string; sizeBytes: number }
  ) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { storageKey: string; uploadUrl: string; expiresIn: number };
    }>(`/api/admin/tickets/${encodeURIComponent(ticketId)}/attachments/presign`, {
      method: 'POST',
      body: input,
    }),

  // Transfer ticket
  transferTicket: (ticketId: string, fromAdminId: string, toAdminId: string, reason?: string) =>
    apiRequest<{
      success: boolean;
      data: {
        ticket: any;
      };
    }>(`/api/admin/tickets/${ticketId}/transfer`, {
      method: 'PATCH',
      body: { fromAdminId, toAdminId, reason },
    }),

  // Update tags
  updateTags: (ticketId: string, tags: string[]) =>
    apiRequest<{
      success: boolean;
      data: {
        ticket: any;
      };
    }>(`/api/admin/tickets/${ticketId}/tags`, {
      method: 'PATCH',
      body: { tags },
    }),

  // Upload attachment
  uploadAttachment: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest<{
      success: boolean;
      data: {
        attachment: any;
      };
    }>(`/api/admin/tickets/${ticketId}/attachments`, {
      method: 'POST',
      body: formData,
    });
  },

  // Get admins
  getAdmins: () =>
    apiRequest<{
      success: boolean;
      data: {
        admins: any[];
      };
    }>('/api/admin/users/admins'),

  // Get ticket statistics
  getStats: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        statusStats: Array<{ _id: string; count: number }>;
        priorityStats: Array<{ _id: string; count: number }>;
        categoryStats: Array<{ _id: string; count: number }>;
        recentTickets: number;
        totalTickets: number;
        weekOverWeekPct?: number;
      };
    }>('/api/admin/tickets/stats'),
};

/** Program flyer download leads (admin queries inbox). */
export const flyerLeadsApi = {
  list: (filters?: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        leads: unknown[];
        pagination: { page: number; limit: number; total: number };
      };
    }>(`/api/admin/queries/flyer-leads${qs ? `?${qs}` : ''}`);
  },

  get: (leadId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { lead: unknown };
    }>(`/api/admin/queries/flyer-leads/${encodeURIComponent(leadId)}`),
};

/** Website lead queries inbox (admin). See docs/ADMIN-PORTAL-QUERIES-FRONTEND.md */
export const leadQueriesApi = {
  list: (filters?: Record<string, string | number | undefined>) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') params.append(key, String(value));
      });
    }
    const qs = params.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        queries: unknown[];
        pagination: { page: number; limit: number; total: number };
      };
    }>(`/api/admin/queries${qs ? `?${qs}` : ''}`);
  },

  getQuery: (queryId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { query: unknown };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}`),

  getMessages: (queryId: string, opts?: { page?: number; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.page != null) params.set('page', String(opts.page));
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return apiRequest<{
      statusCode: number;
      message: string;
      data: {
        messages: unknown[];
        pagination: { page: number; limit: number; total: number };
      };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}/messages${qs ? `?${qs}` : ''}`);
  },

  assign: (queryId: string, body?: { adminId?: string }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { query: unknown };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}/assign`, {
      method: 'PUT',
      body: body ?? {},
    }),

  close: (queryId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { query: unknown };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}/close`, {
      method: 'PATCH',
    }),

  sendMessage: (queryId: string, message: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { message: unknown };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}/messages`, {
      method: 'POST',
      body: { message },
    }),

  getPresence: (queryId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: {
        participants: Array<{
          userId: string;
          role: string;
          isPresent: boolean;
          lastSeenAt?: string;
          socketCount: number;
        }>;
      };
    }>(`/api/admin/queries/${encodeURIComponent(queryId)}/presence`),
};

/** Bot FAQ management (admin). See docs/ADMIN-PORTAL-FAQ-FRONTEND.md */
export type FaqApiWritePayload = {
  question: string;
  answer: string;
  tags?: string[];
  synonyms?: string[];
  scope?: 'global' | 'program_specific';
  programIds?: string[];
  isActive?: boolean;
};

export type FaqListResponse = {
  statusCode: number;
  message: string;
  data: { faqs: unknown[]; pagination: { page: number; limit: number; total: number } };
};

function adminFaqsListRequest(filters?: Record<string, string | number | boolean | undefined>) {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      params.append(key, String(value));
    });
  }
  const qs = params.toString();
  return apiRequest<FaqListResponse>(`/api/admin/faqs${qs ? `?${qs}` : ''}`);
}

/**
 * No GET /api/admin/faqs/:id today — walk paginated list until `_id` matches (unfiltered list).
 */
async function adminFaqFindByIdViaList(faqId: string): Promise<unknown | null> {
  const pageLimit = 100;
  const maxPages = 500;
  let page = 1;
  while (page <= maxPages) {
    const res = await adminFaqsListRequest({ page, limit: pageLimit });
    if (res.statusCode !== 200 || !res.data?.faqs) return null;
    const hit = res.data.faqs.find((row) => {
      const id = row && typeof row === 'object' && '_id' in row ? String((row as { _id: unknown })._id) : '';
      return id === faqId;
    });
    if (hit) return hit;
    const total = res.data.pagination?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageLimit));
    if (page >= totalPages) return null;
    page += 1;
  }
  return null;
}

export const faqsApi = {
  list: (filters?: Record<string, string | number | boolean | undefined>) => adminFaqsListRequest(filters),

  findByIdViaList: (faqId: string) => adminFaqFindByIdViaList(faqId),

  create: (payload: FaqApiWritePayload) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { faq: unknown };
    }>('/api/admin/faqs', { method: 'POST', body: payload }),

  update: (faqId: string, payload: Partial<FaqApiWritePayload>) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { faq: unknown };
    }>(`/api/admin/faqs/${encodeURIComponent(faqId)}`, { method: 'PATCH', body: payload }),

  deactivate: (faqId: string) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { faq: unknown };
    }>(`/api/admin/faqs/${encodeURIComponent(faqId)}/deactivate`, { method: 'PATCH' }),
};

/** Lead qualification flow (admin). See docs/ADMIN-PORTAL-LEAD-QUALIFICATION-FLOW-FRONTEND.md */
export const qualificationFlowApi = {
  getActive: () =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { flow: unknown | null };
    }>('/api/admin/qualification-flow'),

  upsert: (payload: { startNodeId: string; nodes: unknown[] }) =>
    apiRequest<{
      statusCode: number;
      message: string;
      data: { flow: unknown };
    }>('/api/admin/qualification-flow', { method: 'PUT', body: payload }),
};

// Admin management API: roles and staff
export const adminManagementApi = {
  // Roles
  getRoles: () =>
    apiRequest<{ statusCode: number; message: string; data: { roles: any[] } }>(
      '/api/admin/management/roles'
    ),
  createRole: (payload: any) =>
    apiRequest<{ statusCode: number; message: string; data: { role: any } }>(
      '/api/admin/management/roles',
      { method: 'POST', body: payload }
    ),
  updateRole: (roleId: string, payload: any) =>
    apiRequest<{ statusCode: number; message: string; data: { role: any } }>(
      `/api/admin/management/roles/${roleId}`,
      { method: 'PUT', body: payload }
    ),
  deleteRole: (roleId: string) =>
    apiRequest<{ statusCode: number; message: string }>(
      `/api/admin/management/roles/${roleId}`,
      { method: 'DELETE' }
    ),

  // Staff
  getStaff: () =>
    apiRequest<{ statusCode: number; message: string; data: { staff: any[] } }>(
      '/api/admin/management/staff'
    ),
  createStaff: (payload: any) =>
    apiRequest<{ statusCode: number; message: string; data: { staff: any } }>(
      '/api/admin/management/staff',
      { method: 'POST', body: payload }
    ),
  updateStaff: (staffId: string, payload: any) =>
    apiRequest<{ statusCode: number; message: string; data: { staff: any } }>(
      `/api/admin/management/staff/${staffId}`,
      { method: 'PUT', body: payload }
    ),
  deleteStaff: (staffId: string) =>
    apiRequest<{ statusCode: number; message: string }>(
      `/api/admin/management/staff/${staffId}`,
      { method: 'DELETE' }
    ),
};

// Function to notify when auth is invalid